import { rankingStageLabel } from "@/lib/ranking-stage";
import { TRIAL_CONTACT_COUNT } from "@/lib/trial";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FALLBACK_DELIVERY_START, safeUserMessage } from "@/lib/user-error";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  DealExcludedDomains,
  type DealExcludedDomainsHandle,
} from "@/components/deal-excluded-domains";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { TagPicker } from "@/components/tag-picker";
import { computeTagDiff } from "@/lib/tag-diff";
import { estimateRunMinutes } from "@/lib/run-estimate";
import {
  PlanTagSummary,
  normalizeClientBranch,
  branchLabel,
} from "@/components/plan-tag-summary";
import { RaiseSummary } from "@/components/raise-summary";
import {
  Loader2,
  Check,
  AlertTriangle,
  Download,
  ChevronDown,
  ChevronRight,
  Info,
} from "lucide-react";
import {
  adjustPlan,
  startPipeline,
  retryRun,
  getRun,
  refreshDownload,
  refineRun,
  cancelRun,
  updatePlanTags,
  getPlanVocabulary,
  getRunCompletionSummary,
  updateLpTargets,
  updateLoanTargets,
} from "@/lib/run.functions";
import { updateReTargets } from "@/lib/run.functions";
import { ReTargetSelector } from "@/components/re-target-selector";
import {
  computeDefaultLpTargets,
  type LpTarget,
} from "@/lib/lp-targets";
import { LpTargetSelector } from "@/components/lp-target-selector";
import { LoanTargetingLadder } from "@/components/loan-targeting-ladder";
import { LoanTargetSelector } from "@/components/loan-target-selector";
import {
  ALL_LENDING_TYPES,
  resolveLadder,
  type LoanTypePickKind,
} from "@/lib/loan-taxonomy";
import { track } from "@/lib/track";
import { createDeal, createRun, getDealDetail } from "@/lib/deals.functions";
import { listMyDeals, renameDeal, getMyLatestSenderInfo } from "@/lib/deals.functions";
import { checkDealNameCollision } from "@/lib/deals.functions";
import {
  DealNameCollisionDialog,
  type DealNameCollision,
} from "@/components/deal-name-collision-dialog";
import { createRetryLock } from "@/lib/retry-lock";
import { getMyCredits } from "@/lib/account.functions";
import { friendlyAnthropicErrorMessage } from "@/lib/anthropic-error";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SectionHead } from "@/components/ui/section-head";
import { Textarea } from "@/components/ui/textarea";
import { Coachmark } from "@/components/coachmark";
import { useOnboarding } from "@/lib/onboarding";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { InvestorTypesPicker } from "@/components/investor-types-picker";
import {
  InvestorHqLocationPicker,
  HQ_PENDING_TEXT_ERROR,
  commitGeoText,
} from "@/components/investor-hq-location-picker";
import { HQ_LOCATION_HELPER } from "@/components/deal-hq-location-section";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MAX_DESCRIPTION_CHARS,
} from "@/lib/intake-limits";
import { FileField } from "@/components/file-field";
import {
  getPendingOnboarding,
  consumePendingOnboarding,
} from "@/lib/onboarding-handoff.functions";
import { addIncludedDomains } from "@/lib/included-domains.functions";
import { handoffSentences } from "@/lib/finder-handoff";
const VALID_INVESTOR_PHRASES = [
  "equity investors in your company",
  "debt investors/lenders for your company",
  "asset-based lenders or structured credit investors in your assets",
  "equity investors in your real estate",
  "debt investors/lenders in your real estate",
  "limited partners for your fund",
  "seed or GP stake investors for your fund",
] as const;

export const Route = createFileRoute("/_authed/app")({
  // Optional prefill carried over from the ranking demo deal banner.
  validateSearch: (
    search: Record<string, unknown>,
  ): { ranking_category?: string; ranking_stage?: string; runId?: string; finder?: string } => {
    const out: {
      ranking_category?: string;
      ranking_stage?: string;
      runId?: string;
      finder?: string;
    } = {};
    if (typeof search.finder === "string" && search.finder)
      out.finder = search.finder.slice(0, 8);
    if (typeof search.ranking_category === "string" && search.ranking_category)
      out.ranking_category = search.ranking_category.slice(0, 80);
    if (typeof search.ranking_stage === "string" && search.ranking_stage)
      out.ranking_stage = search.ranking_stage.slice(0, 40);
    if (typeof search.runId === "string" && search.runId)
      out.runId = search.runId;
    return out;
  },
  head: () => ({
    meta: [
      { title: "Deal Builder: CapitalDart" },
      { name: "description", content: "Internal tool for building investor outreach packages." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: OutreachBuilder,
});

type View = "entry" | "upload" | "continue" | "plan" | "running" | "download" | "refine";

// PlanTagSummary, normalizeClientBranch, and branchLabel are imported from
// the shared @/components/plan-tag-summary module so the deal-detail page
// can reuse them without divergence.

/**
 * Non-blocking "what we parsed" panel shown on the summary (plan) page.
 * Always informational — never gates the primary CTA. Includes a small
 * re-upload escape hatch that returns the user to the upload form.
 */
function ParsedFromMaterials({
  plan,
  onReupload,
}: {
  plan: PlanJson;
  onReupload: () => void;
}) {
  return (
    <section className="rounded-md border border-border bg-muted/20 p-4">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold">Parsed from your materials</h3>
        <button
          type="button"
          onClick={onReupload}
          className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          Not right? Re-upload
        </button>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        This is what the parser extracted. The plan and outreach are based on
        these tags — re-upload if anything looks wrong, otherwise continue.
      </p>
      <PlanTagSummary plan={plan} />
    </section>
  );
}

interface UploadedFile {
  name: string;
  category: "presentation" | "memo" | "template";
  base64: string;
  size: number;
}

/**
 * Default deal name computed in the user's local timezone at the moment
 * of deal creation. Format: `Deal YYYY-MM-DD HH:MM`. The client is the
 * source of truth for naming - server does not auto-generate.
 */
function defaultDealName(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `Deal ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface PlanJson {
  dealSummary: string;
  investorTypePhrase: string;
  voiceDescriptor: string;
  estimatedMinutes: number;
  investorTypeFallback?: boolean;
  // Step 4 additive tag fields
  branch?: string;
  candidateBranches?: string[];
  branchAmbiguity?: string;
  industryTypes?: string[];
  companyTypes?: string[];
  verticalTypes?: string[];
  stageTypes?: string[];
  fundTypes?: string[];
  fundInvestorTypes?: string[];
  lpTargets?: LpTarget[];
  realEstateEquityOrDebt?: string[];
  realEstatePropertyTypes?: string[];
  loanTypes?: string[];
  location?: string;
  otherPreferences?: string[];
  uncertainTags?: string[];
  dealName?: string;
  raiseAmount?: string;
  typeFilterSummary?: string;
  materialsSufficient?: boolean;
  insufficientReason?: string;
  typeFilterShortfall?: {
    delivered: number;
    requested: number;
    summary: string;
  };
}

interface RunRow {
  id: string;
  deal_id?: string;
  deal_name?: string | null;
  delivery_target?: number | null;
  status: string;
  current_stage: number;
  plan_json: PlanJson | null;
  investor_results: Array<Record<string, string>> | null;
  contact_results?: Array<Record<string, unknown>> | null;
  error_message: string | null;
  error_stage: number | null;
  final_xlsx_url: string | null;
  created_at: string;
  deal_context?: string | null;
  input_tokens_total?: number | null;
  output_tokens_total?: number | null;
  cost_usd?: number | string | null;
  stage_costs?: Record<string, { input: number; output: number; cost: number }> | null;
  total_contacts_loaded?: number | null;
}

/**
 * Sanitize a deal name into a filesystem-safe slug for download filenames.
 * Preserves case, strips OS-illegal chars + quotes, collapses runs of
 * whitespace/punctuation to a single underscore, and caps at 60 chars to
 * stay well under typical OS path length limits.
 * Example: "ARS Capital Aircraft" → "ARS_Capital_Aircraft".
 */
function slugifyDealName(name: string | null | undefined): string {
  if (!name) return "deal";
  const s = name
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/['`]+/g, "")
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60)
    .replace(/_+$/g, "");
  return s || "deal";
}

function OutreachBuilder() {
  const [view, setView] = useState<View>("entry");
  const [runId, setRunId] = useState<string | null>(null);
  const [runDealId, setRunDealId] = useState<string | null>(null);
  const [continueDealId, setContinueDealId] = useState<string | null>(null);
  const [dealName, setDealName] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelDestination, setCancelDestination] = useState<"dashboard" | "entry">("entry");
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const [insufficientReason, setInsufficientReason] = useState<"credits">("credits");
  const navigate = useNavigate();

  const { user } = useAuth();
  const fetchCredits = useServerFn(getMyCredits);
  const creditsQuery = useQuery({
    queryKey: ["my-credits", user?.id],
    queryFn: () => fetchCredits(),
    enabled: Boolean(user?.id),
    staleTime: 30_000,
  });

  /**
   * Gate any "start a new run" action behind a credits/trial check. Returns
   * true when the user has enough to proceed; otherwise opens the buy-credits
   * modal and returns false.
   */
  function gateStartRun(): boolean {
    const c = creditsQuery.data;
    if (!c) return true; // not yet loaded - don't block; server-side also validates
    const creditsOut = (c.credits_remaining ?? 0) <= 0;
    const trialAvailable = c.trial_available ?? false;
    if (!creditsOut || trialAvailable) return true;
    setInsufficientReason("credits");
    setInsufficientOpen(true);
    return false;
  }

  // Deep link: ?runId=<uuid> opens the existing run in the Running view so
  // operators can jump straight to retry/cancel actions.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("runId");
    const dealId = params.get("dealId");
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    let cancelled = false;
    async function loadFromUrl() {
      // Invariant: keep the search param only on a successful, ownership-
      // verified mount of the in-flight view. Every other outcome (no
      // param, invalid uuid, server reject for non-owner, network error)
      // ends with the entry view and a clean URL.
      let mountedInFlight = false;
      if (id && uuidRe.test(id)) {
        try {
          const r = await getRun({ data: { runId: id } });
          if (cancelled) return;
          setRunId(id);
          setRunDealId((r as { deal_id?: string | null })?.deal_id ?? null);
          setDealName((r as { deal_name?: string | null })?.deal_name ?? "");
          setView("running");
          mountedInFlight = true;
        } catch {
          if (cancelled) return;
          setRunId(null);
          setView("entry");
        }
      } else if (dealId && uuidRe.test(dealId)) {
        try {
          const detail = await getDealDetail({ data: { dealId } });
          if (cancelled) return;
          setContinueDealId(dealId);
          setDealName((detail.deal as { name?: string } | null)?.name ?? "");
          setView("continue");
          mountedInFlight = true;
        } catch {
          if (cancelled) return;
          setContinueDealId(null);
          setView("entry");
        }
      }
      if (cancelled) return;
      if (!mountedInFlight && typeof window !== "undefined" && window.location.search) {
        window.history.replaceState(null, "", window.location.pathname);
      }
    }
    loadFromUrl();
    return () => {
      cancelled = true;
    };
  }, []);

  function resetToUpload() {
    setRunId(null);
    setRunDealId(null);
    setDealName("");
    setContinueDealId(null);
    setView("entry");
    if (typeof window !== "undefined" && window.location.search) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }

  const isInFlightView = view === "plan" || view === "running" || view === "refine";

  function requestCancel(destination: "dashboard" | "entry") {
    if (runId && isInFlightView) {
      setCancelDestination(destination);
      setCancelError(null);
      setConfirmOpen(true);
    } else if (destination === "dashboard") {
      navigate({ to: "/dashboard" });
    } else {
      resetToUpload();
    }
  }

  async function confirmCancel() {
    if (!runId) {
      setConfirmOpen(false);
      if (cancelDestination === "dashboard") navigate({ to: "/dashboard" });
      else resetToUpload();
      return;
    }
    setCancelBusy(true);
    setCancelError(null);
    try {
      await cancelRun({ data: { runId } });
      setCancelBusy(false);
      setConfirmOpen(false);
      if (cancelDestination === "dashboard") navigate({ to: "/dashboard" });
      else resetToUpload();
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : "Cancel failed");
      setCancelBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-6 py-5">
          <div className="flex min-w-0 flex-col gap-1">
            {runDealId && (
              <Link
                to="/deals/$dealId"
                params={{ dealId: runDealId }}
                className="text-[12px] text-muted-foreground no-underline hover:text-foreground"
              >
                ← Back to deal
              </Link>
            )}
            <h1 className="text-2xl font-semibold tracking-tight">Deal Builder</h1>
            {dealName && (
              <p className="text-sm text-muted-foreground truncate" title={dealName}>
                {dealName}
              </p>
            )}
          </div>
          {isInFlightView && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => requestCancel("dashboard")}
                className="rounded-md border border-input bg-background px-3 py-2 text-[13.5px] font-medium hover:bg-accent"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => requestCancel("entry")}
                className="rounded-md border border-input bg-background px-3 py-2 text-[13.5px] font-medium hover:bg-accent"
              >
                Start over
              </button>
            </div>
          )}
        </div>
      </header>
      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!cancelBusy) setConfirmOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this deal?</AlertDialogTitle>
            <AlertDialogDescription>
              Any credits already consumed will not be refunded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {cancelError && (
            <p className="text-sm text-destructive">{cancelError}</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelBusy}>Keep running</AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelBusy}
              onClick={(e) => {
                e.preventDefault();
                void confirmCancel();
              }}
            >
              {cancelBusy
                ? "Canceling…"
                : cancelDestination === "dashboard"
                  ? "Cancel"
                  : "Cancel and start over"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={insufficientOpen} onOpenChange={setInsufficientOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              You're out of credits
            </AlertDialogTitle>
            <AlertDialogDescription>
              You don't have enough credits to start a new deal. Top up to
              continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not now</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                setInsufficientOpen(false);
                navigate({ to: "/buy-credits" });
              }}
            >
              Buy credits
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <main className="mx-auto max-w-3xl px-6 py-10">
        {view === "entry" && (
          <EntryView
            onNewDeal={() => {
              if (gateStartRun()) setView("upload");
            }}
            onContinueDeal={() => setView("continue")}
          />
        )}
        {view === "continue" && (
          <ContinueView
            initialDealId={continueDealId}
            onStarted={({ runId, dealId, dealName }) => {
              setRunId(runId);
              setRunDealId(dealId);
              setDealName(dealName);
              setView("running");
            }}
            onCancel={resetToUpload}
            onNewDeal={() => {
              if (gateStartRun()) setView("upload");
            }}

          />
        )}
        {view === "upload" && (
          <UploadView
            onSubmitted={({ runId, dealId, dealName }) => {
              setRunId(runId);
              setRunDealId(dealId);
              setDealName(dealName);
              setView("plan");
            }}
          />
        )}
        {view === "plan" && runId && (
          <PlanView
            runId={runId}
            onRun={() => setView("running")}
            onCancel={resetToUpload}
            onDealName={setDealName}
            gateStartRun={gateStartRun}
          />
        )}
        {view === "running" && runId && (
          <RunningView
            runId={runId}
            onComplete={() => setView("download")}
            onNeedsRefinement={() => setView("refine")}
            onStartOver={resetToUpload}
          />
        )}
        {view === "download" && runId && <DownloadView runId={runId} onAnother={resetToUpload} />}
        {view === "refine" && runId && (
          <RefineView
            runId={runId}
            onContinue={() => setView("running")}
            onCancel={resetToUpload}
          />
        )}
      </main>
    </div>
  );
}

/* ---------------- Entry View ---------------- */

function EntryView({
  onNewDeal,
  onContinueDeal,
}: {
  onNewDeal: () => void;
  onContinueDeal: () => void;
}) {
  return (
    <div className="space-y-6">
      <h2 className="font-serif text-3xl tracking-tight text-foreground">
        What are we doing today?
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <button
          onClick={onNewDeal}
          className="rounded-lg border border-brand-line bg-brand-paper-2/40 p-6 text-left transition hover:border-brand-accent hover:bg-brand-paper-2"
        >
          <div className="text-base font-medium text-foreground">Start a new deal</div>
          <p className="mt-2 text-sm text-muted-foreground">
            Upload materials, generate a plan, and build fresh outreach.
          </p>
        </button>
        <button
          onClick={onContinueDeal}
          className="rounded-lg border border-brand-line bg-brand-paper-2/40 p-6 text-left transition hover:border-brand-accent hover:bg-brand-paper-2"
        >
          <div className="text-base font-medium text-foreground">Find more contacts</div>
          <p className="mt-2 text-sm text-muted-foreground">
            Pick a previous deal and pull more contacts (deduped against earlier outreach for this deal).
          </p>
        </button>
      </div>
    </div>
  );
}

/* ---------------- Continue View ---------------- */

/**
 * Per-delivery outreach drafting choice. Checked by default; unchecking it
 * skips outreach drafting for that one delivery only. The price is the same
 * either way.
 */
function DraftOutreachChoice({
  id,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start gap-2.5 border-t border-brand-line pt-4">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v) => onChange(v === true)}
        className="mt-0.5"
      />
      <div className="space-y-1">
        <Label htmlFor={id} className="text-sm font-medium text-foreground">
          Draft personalized outreach for each contact
        </Label>
        <p className="text-xs text-muted-foreground">
          Leave this on to get an email and a LinkedIn message drafted for
          every contact. Turn it off to receive contacts and ratings only.
          The price is the same either way, and this applies to this delivery
          only.
        </p>
      </div>
    </div>
  );
}

function ContinueView({
  initialDealId,
  onStarted,
  onCancel,
  onNewDeal,
}: {
  initialDealId: string | null;
  onStarted: (payload: { runId: string; dealId: string; dealName: string }) => void;
  onCancel: () => void;
  onNewDeal: () => void;
}) {
  const [deals, setDeals] = useState<Array<{ id: string; name: string }> | null>(null);
  const [dealId, setDealId] = useState<string>("");
  const [target, setTarget] = useState("100");
  const [busy, setBusy] = useState(false);
  // Per-delivery choice. Always starts checked, including for later
  // deliveries on the same deal.
  const [draftOutreach, setDraftOutreach] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const excludedRef = useRef<DealExcludedDomainsHandle>(null);
  const createRunFn = useServerFn(createRun);
  const startPipelineFn = useServerFn(startPipeline);
  const fetchDealDetail = useServerFn(getDealDetail);

  // Read-only plan recap for the currently selected deal. Keyed exactly like
  // the Refine Deal Plan page so cache is shared. Disabled until a deal is
  // selected; refetches automatically when the dropdown selection changes.
  const detailQuery = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDealDetail({ data: { dealId } }),
    enabled: dealId !== "",
  });

  useEffect(() => {
    listMyDeals({ data: {} })
      .then((rows) => {
        // Demo deals (ranking demo) have no materials and no plan, so a
        // find-more delivery against them would produce weak results. They
        // are excluded from this screen entirely.
        const eligible = rows.filter((d) => d.is_demo !== true);
        setDeals(eligible.map((d) => ({ id: d.id, name: d.name })));
        const requested = eligible.find((d) => d.id === initialDealId);
        if (requested) setDealId(requested.id);
        else if (eligible.length > 0) setDealId(eligible[0].id);
        else setDealId("");
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load deals"));
  }, [initialDealId]);


  const parsed = Number(target);
  const valid = Number.isInteger(parsed) && parsed >= 10 && parsed <= 1000 && dealId !== "";

  async function handleStart() {
    setBusy(true);
    setError(null);
    try {
      // Save any typed but not yet added domains before matching begins.
      await excludedRef.current?.flushPending();
      const r = await createRunFn({
        data: { dealId, deliveryTarget: parsed, skipOutreach: !draftOutreach },
      });
      const createdRunId = r?.runId ?? r?.run_id;
      if (!createdRunId) {
        throw new Error("Run creation did not return a run id");
      }
      await startPipelineFn({ data: { runId: createdRunId } });
      const selectedName = deals?.find((d) => d.id === dealId)?.name ?? "";
      onStarted({ runId: createdRunId, dealId, dealName: selectedName });
    } catch (e) {
      console.error("[delivery:start] failed", e);
      setError(safeUserMessage(e, FALLBACK_DELIVERY_START));
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <h2 className="font-serif text-3xl tracking-tight">Find more contacts</h2>
      <p className="text-sm text-muted-foreground">
        Uses this deal&rsquo;s plan and materials. Firms and contacts from prior
        contacts already reached for this deal are excluded automatically.
      </p>
      {deals === null ? (
        <div className="text-sm text-muted-foreground"><Loader2 className="inline mr-2 h-4 w-4 animate-spin" />Loading deals…</div>
      ) : deals.length === 0 ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Set up your deal first. Find more investors works on a deal with
            your materials and plan.
          </p>
          <Button onClick={onNewDeal}>Set up your deal</Button>
        </div>

      ) : (
        <>
          <div className="space-y-2">
            <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Deal</label>
            <Select value={dealId} onValueChange={setDealId}>
              <SelectTrigger><SelectValue placeholder="Choose a deal…" /></SelectTrigger>
              <SelectContent>
                {deals.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {dealId !== "" && (
            <ContinuePlanRecap
              dealId={dealId}
              query={detailQuery}
            />
          )}
          <div className="flex flex-col gap-1.5 border-b border-brand-line py-3 last:border-b-0">
            <Label
              htmlFor="number-of-investors"
              className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              Number of investors
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info
                      size={14}
                      className="ml-1.5 inline-flex align-middle text-brand-muted cursor-help"
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="z-50">
                    Each investor costs one credit. One credit delivers one matched investor with the outreach drafted in your voice.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              id="number-of-investors"
              type="number"
              min={10}
              max={1000}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              aria-describedby="number-of-investors-hint"
              className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 text-foreground max-w-[200px] text-[15px]! md:text-[15px]!"
            />
            <p id="number-of-investors-hint" className="text-xs text-muted-foreground">
              10–1000. Default min(100, available credits).
            </p>
          </div>
          <DraftOutreachChoice
            id="continue-draft-outreach"
            checked={draftOutreach}
            onChange={setDraftOutreach}
          />
          {dealId !== "" && (
            <DealExcludedDomains
              ref={excludedRef}
              dealId={dealId}
              collapsible
              title="Excluded firms (optional)"
            />
          )}
        </>
      )}
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{friendlyAnthropicErrorMessage(error)}</div>
      )}
      <div className="flex gap-3">
        <Button onClick={handleStart} disabled={!valid || busy}>
          {busy ? "Starting…" : "Build outreach"}
        </Button>
        <button onClick={onCancel} className="text-sm text-muted-foreground underline hover:text-foreground">Cancel</button>
      </div>
    </div>
  );
}

/* ---------------- Continue Plan Recap ---------------- */

type MaterialCat = "presentation" | "memo" | "template";
const RECAP_SLOT_LABELS: Record<MaterialCat, string> = {
  presentation: "Investor presentation",
  memo: "Memo or supporting materials",
  template: "Template email",
};

function ContinuePlanRecap({
  dealId,
  query,
}: {
  dealId: string;
  query: ReturnType<typeof useQuery<Awaited<ReturnType<typeof getDealDetail>>>>;
}) {
  if (query.isLoading) {
    return (
      <div className="rounded-md border border-brand-line bg-brand-paper p-3 text-xs text-muted-foreground">
        <Loader2 className="mr-2 inline h-3.5 w-3.5 animate-spin" />
        Loading this deal&rsquo;s plan…
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <div className="rounded-md border border-brand-line bg-brand-paper p-3 text-xs text-muted-foreground">
        Could not load this deal&rsquo;s plan.
      </div>
    );
  }

  const { deal } = query.data as { deal: Record<string, unknown> };
  const planJson = (deal as { plan_json?: unknown }).plan_json as
    | (Record<string, unknown> & { dealSummary?: string; branch?: string })
    | undefined;
  const parseReport = (Array.isArray((deal as { parse_report?: unknown }).parse_report)
    ? ((deal as { parse_report: unknown[] }).parse_report as Array<{
        filename: string;
        category: MaterialCat;
      }>)
    : []
  ).filter(
    (e) =>
      e && (e.category === "presentation" || e.category === "memo" || e.category === "template"),
  );
  const materialBySlot = (cat: MaterialCat) =>
    parseReport.find((e) => e.category === cat) ?? null;

  const hasPlan =
    !!planJson &&
    (Boolean(planJson.dealSummary) ||
      Boolean(planJson.branch) ||
      Object.keys(planJson).length > 0);

  if (!hasPlan) {
    return (
      <div className="rounded-md border border-brand-line bg-brand-paper p-3 text-xs text-muted-foreground">
        This deal does not have a saved plan yet.{" "}
        <Link
          to="/deals/$dealId/refine"
          params={{ dealId }}
          className="underline hover:text-foreground"
        >
          Refine plan
        </Link>
      </div>
    );
  }

  const branch = normalizeClientBranch(
    typeof planJson?.branch === "string" ? (planJson.branch as string) : undefined,
  );
  const isRealEstate = branch === "RealEstate";

  return (
    <section className="space-y-3 rounded-md border border-brand-line bg-brand-paper p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          This outreach will use:
        </h3>
        <Link
          to="/deals/$dealId/refine"
          params={{ dealId }}
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          Refine plan
        </Link>
      </div>

      <RaiseSummary plan={planJson as { dealSummary?: string }} />

      <PlanTagSummary
        plan={planJson as Parameters<typeof PlanTagSummary>[0]["plan"]}
      />

      {isRealEstate && (
        <p className="text-[12px] italic text-muted-foreground">
          Property and capital types are listed in priority order; the first item is primary.
        </p>
      )}

      <div className="space-y-1.5 rounded-md border border-brand-line/60 bg-brand-paper-2/40 px-3 py-2.5">
        <div className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
          Current materials
        </div>
        <ul className="space-y-1">
          {(["presentation", "memo", "template"] as MaterialCat[]).map((cat) => {
            const cur = materialBySlot(cat);
            return (
              <li
                key={cat}
                className="flex items-center justify-between gap-3 text-[14px]"
              >
                <span className="min-w-0 truncate text-foreground">
                  <span className="text-muted-foreground">{RECAP_SLOT_LABELS[cat]}:</span>{" "}
                  {cur ? (
                    <span>{cur.filename}</span>
                  ) : (
                    <span className="italic text-muted-foreground">none</span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

/* ---------------- Upload View ---------------- */

function UploadView({
  onSubmitted,
}: {
  onSubmitted: (payload: { runId: string; dealId: string; dealName: string }) => void;
}) {
  const [presentation, setPresentation] = useState<UploadedFile | null>(null);
  const [memo, setMemo] = useState<UploadedFile | null>(null);
  const [template, setTemplate] = useState<UploadedFile | null>(null);
  // Prefilled from the ranking demo deal banner when present.
  const rankingPrefill = Route.useSearch();
  const [description, setDescription] = useState(() => {
    const cat = rankingPrefill.ranking_category;
    const stg = rankingPrefill.ranking_stage;
    if (!cat && !stg) return "";
    return [cat ? `Sector: ${cat}.` : null, stg ? `Stage: ${rankingStageLabel(stg)}.` : null]
      .filter(Boolean)
      .join(" ");
  });
  const [dealNameInput, setDealNameInput] = useState("");
  const [investorTypesRequest, setInvestorTypesRequest] = useState("");
  const [investorHqLocation, setInvestorHqLocation] = useState<string[]>([]);
  const [hqPendingText, setHqPendingText] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const typesInputRef = useRef<HTMLInputElement>(null);
  const [userEmail, setUserEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [role, setRole] = useState("");
  const [firm, setFirm] = useState("");
  const { user } = useAuth();
  // Homepage Top 10 finder selections kept from before signup.
  const fetchPendingOnboarding = useServerFn(getPendingOnboarding);
  const consumeOnboardingFn = useServerFn(consumePendingOnboarding);
  const addIncludedDomainsFn = useServerFn(addIncludedDomains);
  const pendingOnboarding = useQuery({
    queryKey: ["pending-onboarding", user?.id],
    queryFn: () => fetchPendingOnboarding(),
    enabled: !!user,
    staleTime: 60_000,
  });
  const keptSearch =
    pendingOnboarding.data && !pendingOnboarding.data.consumed
      ? pendingOnboarding.data.handoff
      : null;
  const keptSentences = keptSearch ? handoffSentences(keptSearch) : "";
  useEffect(() => {
    if (!keptSentences) return;
    setDescription((prev) => (prev === "" ? keptSentences : prev));
  }, [keptSentences]);
  const fetchLatestSender = useServerFn(getMyLatestSenderInfo);
  // Prefill email from auth user (does not overwrite user input).
  useEffect(() => {
    const email = user?.email;
    if (!email) return;
    setUserEmail((prev) => (prev === "" ? email : prev));
  }, [user?.email]);
  // Prefill sender fields from the user's most recent deal (best-effort).
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    fetchLatestSender()
      .then((info) => {
        if (cancelled || !info) return;
        if (info.firstName) setFirstName((prev) => (prev === "" ? info.firstName! : prev));
        if (info.role) setRole((prev) => (prev === "" ? info.role! : prev));
        if (info.firm) setFirm((prev) => (prev === "" ? info.firm! : prev));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id, fetchLatestSender]);
  const fetchCreditsUV = useServerFn(getMyCredits);
  const creditsQueryUV = useQuery({
    queryKey: ["my-credits", user?.id],
    queryFn: () => fetchCreditsUV(),
    enabled: Boolean(user?.id),
    staleTime: 30_000,
  });
  const availableCredits = creditsQueryUV.data?.credits_remaining ?? null;
  const trialAvailable = creditsQueryUV.data?.trial_available ?? false;
  const [deliveryTargetInput, setDeliveryTargetInput] = useState("");
  const [targetTouched, setTargetTouched] = useState(false);
  // Per-delivery outreach drafting choice, checked by default.
  const [draftOutreachNew, setDraftOutreachNew] = useState(true);
  // Default to min(100, credits_remaining) once credits load, unless user typed.
  useEffect(() => {
    if (targetTouched) return;
    if (availableCredits === null) return;
    const def = Math.max(0, Math.min(100, availableCredits));
    setDeliveryTargetInput(String(def));
  }, [availableCredits, targetTouched]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createDealFn = useServerFn(createDeal);
  const checkNameFn = useServerFn(checkDealNameCollision);
  const [nameCollision, setNameCollision] = useState<DealNameCollision | null>(null);
  const createRunFn = useServerFn(createRun);
  const errorBannerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error && errorBannerRef.current) {
      errorBannerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  const hasContent = presentation !== null || memo !== null || description.trim().length > 0;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail.trim());
  const parsedTarget = Number(deliveryTargetInput);
  const targetValid = trialAvailable
    ? true
    : (deliveryTargetInput.trim() !== "" &&
       Number.isInteger(parsedTarget) &&
       parsedTarget >= 10 &&
       parsedTarget <= 1000);
  const exceedsCredits = trialAvailable
    ? false
    : (targetValid && availableCredits !== null && parsedTarget > availableCredits);
  const outOfCredits = availableCredits === 0;
  const canSubmit = trialAvailable
    ? (!submitting && emailValid && firstName.trim() && role.trim() && hasContent)
    : (!submitting && emailValid && firstName.trim() && role.trim() && hasContent && targetValid && !exceedsCredits);

  async function handleSubmit(skipNameCheck = false) {
    setError(null);
    let hqLocations = investorHqLocation;
    if (hqPendingText.trim()) {
      const committed = commitGeoText(hqPendingText.trim(), investorHqLocation);
      if (!committed) {
        setError(HQ_PENDING_TEXT_ERROR);
        return;
      }
      hqLocations = committed;
      setInvestorHqLocation(committed);
      setHqPendingText("");
    }
    if (!trialAvailable && !targetValid) {
      setError("Number of investors must be an integer between 10 and 1000.");
      return;
    }
    const proposedName = dealNameInput.trim().slice(0, 80);
    if (!skipNameCheck && proposedName) {
      try {
        const res = await checkNameFn({ data: { name: proposedName } });
        if (res.collision) {
          setNameCollision(res as DealNameCollision);
          return;
        }
      } catch {
        // Name check is advisory only; never block creation on it.
      }
    }
    setSubmitting(true);
    try {
      const files = [presentation, memo, template].filter((f): f is UploadedFile => f !== null);
      const trimmedName = dealNameInput.trim().slice(0, 80);
      // [prepipeline_client:submit] — start of A (upload/network + create-deal serverFn).
      // Paired with plan_review_mount and click_run below to isolate client
      // network + user dwell from server compute.
      // eslint-disable-next-line no-console
      console.log("prepipeline_client", "submit", {
        t_ms: performance.now(),
        ts_iso: new Date().toISOString(),
        file_count: files.length,
        total_b64_chars: files.reduce((n, f) => n + f.base64.length, 0),
      });
      const dealResult = await createDealFn({
        data: {
          files: files.map((f) => ({
            name: f.name,
            category: f.category,
            base64: f.base64,
          })),
          description,
          investorTypesRequest: investorTypesRequest.trim() || undefined,
          investorHqLocation: hqLocations.length > 0 ? hqLocations : undefined,
          firstName,
          role,
          firm,
          userEmail,
          name: trimmedName || defaultDealName(),
          ...(keptSearch ? { finderAnswers: keptSearch } : {}),
        },
      });
      const createdDealId = dealResult?.dealId ?? dealResult?.deal_id;
      if (!createdDealId) {
        throw new Error("Deal creation did not return a deal id");
      }
      // The firms from the Top 10 list the visitor was looking at become
      // the deal's included list, so the first delivery contains them.
      // Best effort: a failure here must not stop the deal being created.
      if (keptSearch?.firms && keptSearch.firms.length > 0) {
        try {
          await addIncludedDomainsFn({
            data: {
              dealId: createdDealId,
              domains: keptSearch.firms
                .map((f) => f.domain)
                .filter((d) => d.length > 0),
              firms: keptSearch.firms,
            },
          });
        } catch {
          // the deal is created either way; the user can add firms by hand
        }
      }
      // The kept Top 10 selections are now on the deal, so never reapply them.
      if (keptSearch) {
        try {
          await consumeOnboardingFn();
        } catch {
          // best effort: the prefill already landed on the deal
        }
      }
      const runResult = await createRunFn({
        data: {
          dealId: createdDealId,
          deliveryTarget: trialAvailable ? undefined : parsedTarget,
          skipOutreach: !draftOutreachNew,
        },
      });
      const createdRunId = runResult?.runId ?? runResult?.run_id;
      if (!createdRunId) {
        throw new Error("Run creation did not return a run id");
      }
      // Parsing runs but never blocks. The parse summary is surfaced on
      // the plan/summary page as a non-blocking note with a re-upload
      // escape hatch — see PlanView.
      onSubmitted({
        runId: createdRunId,
        dealId: createdDealId,
        dealName: trimmedName || defaultDealName(),
      });
    } catch (e) {
      const raw = e instanceof Error ? e.message : "Submission failed";
      const friendly = raw.includes("REGION_UNSUPPORTED")
        ? "Outreach generation isn't available from your current location yet. Our AI provider doesn't support requests from this region. If you're using a VPN, connect to a United States location and try again. Your Investor Readiness Report is unaffected and works from any location."
        : raw;
      setError(friendly);
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-10">
      {submitting ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Reading your materials and generating a plan…
          </p>
          <p className="mt-1 text-xs text-muted-foreground">This usually takes 10–20 seconds.</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            <h2 className="font-serif text-4xl leading-tight tracking-tight text-foreground sm:text-5xl">
              Take a deal to market in minutes.
            </h2>
            <p className="max-w-2xl text-base text-muted-foreground">
              Drop your materials and we&rsquo;ll deliver a vetted investor list with a
              personalised draft for every recipient.
            </p>
          </div>

          <section className="space-y-5">
            <SectionHead
              num="01"
              eyebrow="The deal"
              title="Your materials"
              description="Share whatever you have. Any single file or a written description is enough to start."
            />
            <div className="space-y-2">
              <div className="flex items-baseline justify-between gap-3">
                <label
                  htmlFor="deal-name-input"
                  className="block text-xs font-medium uppercase tracking-wider text-muted-foreground"
                >
                  Deal name
                </label>
                {dealNameInput.length > 60 ? (
                  <span
                    className={`text-xs ${
                      dealNameInput.length >= 80
                        ? "text-destructive"
                        : "text-muted-foreground"
                    }`}
                  >
                    {dealNameInput.length} / 80
                  </span>
                ) : null}
              </div>
              <input
                id="deal-name-input"
                type="text"
                value={dealNameInput}
                maxLength={80}
                onChange={(e) => setDealNameInput(e.target.value)}
                placeholder="Acme Series B, Northwind Credit Fund, Globex SFR - anything that helps you find this later."
                className="block w-full rounded-md border border-brand-line bg-brand-paper-2/40 px-3 py-2.5 text-[15px] text-foreground placeholder:text-muted-foreground/70 shadow-sm transition-colors hover:border-dashed hover:border-brand-accent focus:outline-none focus:border-brand-accent focus:border-solid focus:ring-2 focus:ring-brand-accent/20 focus:bg-brand-paper"
              />
              <p className="text-xs text-muted-foreground">
                Optional - if blank, we&rsquo;ll use a timestamp.
              </p>
            </div>
            <div className="space-y-4 border-t border-brand-line pt-5">
              {!hasContent && (
                <p className="text-xs text-muted-foreground">
                  Provide at least one file or a description of the deal.
                </p>
              )}
              <FileField
                label="Investor presentation or supporting materials"
                hint="PDF or DOCX · optional"
                accept=".pdf,.docx"
                value={presentation}
                onChange={(f) => setPresentation(f ? { ...f, category: "presentation" } : null)}
              />
              <FileField
                label="Supporting materials"
                hint="PDF or DOCX · optional"
                accept=".pdf,.docx"
                value={memo}
                onChange={(f) => setMemo(f ? { ...f, category: "memo" } : null)}
              />
              <FileField
                label="Template email sent to investors"
                hint="PDF or DOCX · optional"
                accept=".pdf,.docx"
                value={template}
                onChange={(f) => setTemplate(f ? { ...f, category: "template" } : null)}
              />
            </div>

            <div className="space-y-2 border-t border-brand-line pt-5">
              <label
                htmlFor="deal-description"
                className="block text-xs font-medium uppercase tracking-wider text-muted-foreground"
              >
                Or describe the deal
              </label>
              <Textarea
                id="deal-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                maxLength={MAX_DESCRIPTION_CHARS}
                placeholder="Sector, stage, structure, size, key terms, anything investors should know."
                className="resize-none rounded-md border border-brand-line bg-brand-paper-2/40 shadow-sm transition-colors hover:border-dashed hover:border-brand-accent focus-visible:border-brand-accent focus-visible:border-solid focus-visible:ring-2 focus-visible:ring-brand-accent/20 focus-visible:bg-brand-paper text-[15px]! leading-[1.6] md:text-[15px]!"
              />
              <div className="text-right text-xs text-muted-foreground">
                {description.length.toLocaleString("en-US")} / {MAX_DESCRIPTION_CHARS.toLocaleString("en-US")}
              </div>
            </div>

            <div className="space-y-2 border-t border-brand-line pt-5">
              <div className="flex items-baseline justify-between gap-3">
                <label
                  htmlFor="deal-investor-types"
                  className="block text-xs font-medium uppercase tracking-wider text-muted-foreground"
                >
                  What types of investors are you targeting?{" "}
                  <span className="ml-1 normal-case tracking-normal text-muted-foreground/70">(optional)</span>
                </label>
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  Browse types
                </button>
              </div>
              <Input
                id="deal-investor-types"
                ref={typesInputRef}
                value={investorTypesRequest}
                onChange={(e) => setInvestorTypesRequest(e.target.value.slice(0, 500))}
                maxLength={500}
                placeholder='e.g., growth equity, single family offices, angels, specialty lenders. Or "all except SPACs". Or "All".'
                className="rounded-md border-brand-line bg-brand-paper-2/40 shadow-sm transition-colors hover:border-dashed hover:border-brand-accent focus-visible:border-brand-accent focus-visible:border-solid focus-visible:ring-2 focus-visible:ring-brand-accent/20 focus-visible:bg-brand-paper text-[15px]! md:text-[15px]!"
              />
              <p className="text-xs text-muted-foreground">
                Plain English. Include what you want, or exclude what you don&rsquo;t. Optional. Blank means &ldquo;All&rdquo;.
              </p>
            </div>

            <div className="space-y-2 border-t border-brand-line pt-5">
              <label
                htmlFor="deal-investor-hq-location"
                className="block text-xs font-medium uppercase tracking-wider text-muted-foreground"
              >
                Investor HQ location{" "}
                <span className="ml-1 normal-case tracking-normal text-muted-foreground/70">(optional)</span>
              </label>
              <InvestorHqLocationPicker
                inputId="deal-investor-hq-location"
                value={investorHqLocation}
                onChange={setInvestorHqLocation}
                onPendingTextChange={setHqPendingText}
              />
              <p className="text-xs text-muted-foreground">{HQ_LOCATION_HELPER}</p>
            </div>
            <InvestorTypesPicker
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              onAdd={(tokens) => {
                if (tokens.length === 0) return;
                const current = investorTypesRequest.trim();
                const existing = new Set(
                  current
                    .split(",")
                    .map((s) => s.trim().toLowerCase())
                    .filter(Boolean),
                );
                const additions = tokens.filter((t) => !existing.has(t.toLowerCase()));
                if (additions.length === 0) {
                  typesInputRef.current?.focus();
                  return;
                }
                const next = current
                  ? `${current}, ${additions.join(", ")}`
                  : additions.join(", ");
                setInvestorTypesRequest(next.slice(0, 500));
                setTimeout(() => typesInputRef.current?.focus(), 0);
              }}
            />

            {trialAvailable ? (
              <div className="border-t border-brand-line pt-5">
                <p className="text-sm text-brand-ink">
                  Your first sample is free:{" "}
                  <span className="font-medium">{TRIAL_CONTACT_COUNT} free contacts</span>,
                  on us.
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Top up credits to run a full deal of 10 to 1000 investors on your next one.
                </p>
              </div>
            ) : availableCredits !== null && availableCredits < 10 ? (
              <div className="space-y-2 border-t border-brand-line pt-5">
                <h3 className="font-serif text-lg text-brand-ink">
                  You need at least 10 credits to start a deal.
                </h3>
                <p className="text-sm text-muted-foreground">
                  You have {availableCredits}. Add credits to continue.
                </p>
                <Button asChild className="mt-1">
                  <Link to="/buy-credits">Buy credits</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-2 border-t border-brand-line pt-5">
                <label
                  htmlFor="delivery-target-input"
                  className="block text-xs font-medium uppercase tracking-wider text-muted-foreground"
                >
                  Number of investors
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info
                          size={14}
                          className="ml-1.5 inline-flex align-middle text-brand-muted cursor-help"
                        />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="z-50">
                        Each investor costs one credit. One credit delivers one matched investor with the outreach drafted in your voice.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </label>
                <input
                  id="delivery-target-input"
                  type="number"
                  min={10}
                  max={1000}
                  step={1}
                  value={deliveryTargetInput}
                  onChange={(e) => { setTargetTouched(true); setDeliveryTargetInput(e.target.value); }}
                  disabled={outOfCredits}
                  title={outOfCredits ? "You're out of credits. Top up to start a new deal." : undefined}
                  aria-invalid={
                    outOfCredits || exceedsCredits || (deliveryTargetInput.trim() !== "" && !targetValid)
                      ? true
                      : undefined
                  }
                  className="block w-full max-w-[200px] rounded-md border border-brand-line bg-brand-paper-2/40 px-3 py-2.5 text-base font-medium tabular-nums text-foreground shadow-sm transition-colors hover:border-dashed hover:border-brand-accent focus:outline-none focus:border-brand-accent focus:border-solid focus:ring-2 focus:ring-brand-accent/20 focus:bg-brand-paper disabled:cursor-not-allowed disabled:opacity-60 aria-invalid:border-destructive aria-invalid:ring-destructive/20"
                />
                {outOfCredits ? (
                  <p className="text-xs text-destructive">You&rsquo;re out of credits. Top up to start a new deal.</p>
                ) : exceedsCredits && availableCredits !== null ? (
                  <p className="text-xs text-destructive">
                    You only have {availableCredits} credits available. Reduce to {availableCredits} or top up.
                  </p>
                ) : deliveryTargetInput.trim() !== "" && !targetValid ? (
                  <p className="text-xs text-destructive">Must be an integer between 10 and 1000.</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    How many outreach rows to deliver (10 to 1000). Defaults to your available credits, up to 100.
                  </p>
                )}
              </div>
            )}
            <DraftOutreachChoice
              id="new-deal-draft-outreach"
              checked={draftOutreachNew}
              onChange={setDraftOutreachNew}
              disabled={outOfCredits}
            />
          </section>

          <section className="space-y-5">
            <SectionHead
              num="02"
              eyebrow="You"
              title="About you"
              description="We use this to personalise every outbound draft."
            />
            <div className="rounded-lg border border-brand-line bg-brand-paper-2/60 shadow-sm">
              <div className="px-4">
                <Field
                  label="First name *"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Jane"
                />
                <div className="flex flex-col gap-1.5 border-b border-brand-line py-3 last:border-b-0">
                  <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Role *
                  </label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="h-auto border-0 bg-transparent px-0 py-0 text-foreground shadow-none focus:ring-0 focus-visible:ring-0 data-[placeholder]:text-muted-foreground/70">
                      <SelectValue placeholder="Select your role…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Banker representing the issuer">
                        Banker representing the issuer
                      </SelectItem>
                      <SelectItem value="Founder/CEO/GP raising directly">
                        Founder / CEO / GP raising directly
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Field
                  label="Firm name"
                  value={firm}
                  onChange={(e) => setFirm(e.target.value)}
                  placeholder="Optional"
                />
                <Field
                  label="Email *"
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="you@example.com"
                  error={
                    userEmail.trim().length > 0 && !emailValid
                      ? "Please enter a valid email address."
                      : undefined
                  }
                />
              </div>
            </div>
          </section>

          {error && (
            <div
              ref={errorBannerRef}
              role="alert"
              aria-live="polite"
              className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {friendlyAnthropicErrorMessage(error)}
            </div>
          )}

          <div className="flex items-center justify-between gap-4 border-t border-brand-line pt-6">
            <p className="text-xs text-muted-foreground">
              By submitting you agree your materials will be processed to draft outreach.
            </p>
            <Button
              size="lg"
              disabled={!canSubmit}
              onClick={() => void handleSubmit()}
              className="min-w-[180px] hover:outline-dashed hover:outline-2 hover:outline-offset-2 hover:outline-brand-accent"
            >
              Build my outreach
            </Button>
          </div>
        </>
      )}
      <DealNameCollisionDialog
        collision={nameCollision}
        onOpenChange={(open) => {
          if (!open) setNameCollision(null);
        }}
        onContinue={() => {
          setNameCollision(null);
          void handleSubmit(true);
        }}
      />
    </div>
  );
}

/* ---------------- Plan View ---------------- */


function PlanView({
  runId,
  onRun,
  onCancel,
  onDealName,
  gateStartRun,
}: {
  runId: string;
  onRun: () => void;
  onCancel: () => void;
  onDealName?: (name: string) => void;
  gateStartRun: () => boolean;
}) {
  return <PlanViewInner runId={runId} onRun={onRun} onCancel={onCancel} onDealName={onDealName} gateStartRun={gateStartRun} />;
}

function PlanViewInner({
  runId,
  onRun,
  onCancel,
  onDealName,
  gateStartRun,
}: {
  runId: string;
  onRun: () => void;
  onCancel: () => void;
  onDealName?: (name: string) => void;
  gateStartRun: () => boolean;
}) {
  const [run, setRun] = useState<RunRow | null>(null);
  const [adjusting, setAdjusting] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dealName, setDealName] = useState<string>("");
  const [storedDealName, setStoredDealName] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [nameCollision, setNameCollision] = useState<DealNameCollision | null>(null);
  const dealNameLoadedRef = useRef(false);
  const planExcludedRef = useRef<DealExcludedDomainsHandle>(null);
  const startPipelineFn = useServerFn(startPipeline);
  const updateLpTargetsFn = useServerFn(updateLpTargets);
  const updateLoanTargetsFn = useServerFn(updateLoanTargets);
  const updateReTargetsFn = useServerFn(updateReTargets);
  const [lpTargets, setLpTargets] = useState<LpTarget[] | null>(null);
  const lpTargetsInitedRef = useRef(false);
  const [loanPickKind, setLoanPickKind] = useState<LoanTypePickKind | null>(null);
  const [loanPrimary, setLoanPrimary] = useState<string | null>(null);
  const [loanChecked, setLoanChecked] = useState<string[]>([]);
  const [loanSeed, setLoanSeed] = useState<string[]>([]);
  const loanInitedRef = useRef(false);

  // Manual tag picker (Step 1): local working-copy state for the four
  // classification fields. Saving is wired in a later step.
  const [pickIndustryTypes, setPickIndustryTypes] = useState<string[]>([]);
  const [pickCompanyTypes, setPickCompanyTypes] = useState<string[]>([]);
  const [pickVerticalTypes, setPickVerticalTypes] = useState<string[]>([]);
  const [pickStageTypes, setPickStageTypes] = useState<string[]>([]);
  const tagInitedRef = useRef(false);

  // RealEstate-only targeting editor state. Seeded once from run.plan_json
  // and re-seeded after each successful save. Order-sensitive dirty check.
  const [rePropertyTypes, setRePropertyTypes] = useState<string[]>([]);
  const [reCapitalTypes, setReCapitalTypes] = useState<string[]>([]);
  const [rePropSeed, setRePropSeed] = useState<string[]>([]);
  const [reCapSeed, setReCapSeed] = useState<string[]>([]);
  const reInitedRef = useRef(false);

  // [prepipeline_client:plan_review_mount] — fires exactly once when the
  // plan-review screen first has a plan_json to render (i.e. server plan
  // generation has landed and the user is about to start reading/editing).
  // End of B+C, start of D (human dwell). Depends only on run + plan_json;
  // the guard makes it fire at most once per PlanView mount.
  const prepipelineReviewMarkerFiredRef = useRef(false);
  useEffect(() => {
    if (prepipelineReviewMarkerFiredRef.current) return;
    if (!run || !run.plan_json) return;
    prepipelineReviewMarkerFiredRef.current = true;
    // eslint-disable-next-line no-console
    console.log("prepipeline_client", "plan_review_mount", {
      t_ms: performance.now(),
      ts_iso: new Date().toISOString(),
      runId,
      status: run.status,
    });
  }, [run, runId]);

  const vocabFn = useServerFn(getPlanVocabulary);
  const vocabQuery = useQuery({
    queryKey: ["planVocabulary"],
    queryFn: () => vocabFn(),
    staleTime: 1000 * 60 * 60,
  });
  const vocab = vocabQuery.data?.vocab;

  const { tour_choice, plan_tour_dismissed_at, markSeen } = useOnboarding();
  // Plan tour only runs for users who explicitly opted into the tour.
  const tourActive =
    tour_choice === "yes" && plan_tour_dismissed_at === null;
  const [tourStep, setTourStep] = useState<0 | 1 | 2 | 3>(1);
  const dismissTour = () => {
    setTourStep(0);
    void markSeen("plan_tour_dismissed_at");
  };

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const r = (await getRun({ data: { runId } })) as RunRow;
        if (!cancelled) setRun(r);
        if (!cancelled && r && !dealNameLoadedRef.current && r.deal_id) {
          dealNameLoadedRef.current = true;
          try {
            const detail = await getDealDetail({ data: { dealId: r.deal_id } });
            const n = (detail.deal as { name?: string } | null)?.name ?? "";
            if (!cancelled) {
              setDealName(n);
              setStoredDealName(n);
              onDealName?.(n);
            }
          } catch {
            /* non-fatal: leave input empty, user can type */
          }
        }
        if (!cancelled && r && r.status === "planning") {
          setTimeout(poll, 1500);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load plan");
      }
    }
    poll();
    return () => {
      cancelled = true;
    };
  }, [runId]);

  // Initialize LP-target selection from the plan (existing selection takes
  // priority; otherwise compute signal-driven defaults). Fund branch only.
  useEffect(() => {
    if (!run || !run.plan_json) return;
    if (lpTargetsInitedRef.current) return;
    const p = run.plan_json;
    if (normalizeClientBranch(p.branch) !== "Fund") return;
    lpTargetsInitedRef.current = true;
    const existing = Array.isArray(p.lpTargets) ? p.lpTargets : null;
    setLpTargets(existing && existing.length > 0 ? existing : computeDefaultLpTargets(p));
  }, [run]);

  // Initialize Loans/Debt loan-type selection from the plan if present.
  useEffect(() => {
    if (!run || !run.plan_json) return;
    if (loanInitedRef.current) return;
    const p = run.plan_json as unknown as Record<string, unknown>;
    if (normalizeClientBranch(typeof p.branch === "string" ? p.branch : undefined) !== "Loans/Debt") return;
    loanInitedRef.current = true;
    const kind = p.loanTypePickKind === "category" ? "category" : "type";
    const primary = typeof p.loanTypePrimary === "string" ? p.loanTypePrimary : null;
    const targets = Array.isArray(p.loanTargets)
      ? (p.loanTargets as unknown[]).map(String)
      : [];
    if (primary) {
      setLoanPickKind(kind);
      setLoanPrimary(primary);
      const seeded = resolveLadder(kind, primary);
      setLoanSeed(seeded);
      setLoanChecked(targets.length > 0 ? targets : seeded);
    }
  }, [run]);

  // Initialize manual tag picker selection from the plan for non-Loans/Debt
  // branches. Mirrors the loan seeding effect above.
  useEffect(() => {
    if (!run || !run.plan_json) return;
    if (tagInitedRef.current) return;
    const p = run.plan_json as unknown as Record<string, unknown>;
    if (normalizeClientBranch(typeof p.branch === "string" ? p.branch : undefined) !== "DirectEquityMezz") return;
    tagInitedRef.current = true;
    const toStrArr = (v: unknown): string[] =>
      Array.isArray(v) ? (v as unknown[]).map(String) : [];
    setPickIndustryTypes(toStrArr(p.industryTypes));
    setPickCompanyTypes(toStrArr(p.companyTypes));
    setPickVerticalTypes(toStrArr(p.verticalTypes));
    setPickStageTypes(toStrArr(p.stageTypes));
  }, [run]);

  // Initialize RealEstate property/capital selection from the plan if present.
  useEffect(() => {
    if (!run || !run.plan_json) return;
    if (reInitedRef.current) return;
    const p = run.plan_json as unknown as Record<string, unknown>;
    if (normalizeClientBranch(typeof p.branch === "string" ? p.branch : undefined) !== "RealEstate") return;
    reInitedRef.current = true;
    const toStrArr = (v: unknown): string[] =>
      Array.isArray(v) ? (v as unknown[]).map(String) : [];
    const props = toStrArr(p.realEstatePropertyTypes);
    const caps = toStrArr(p.realEstateEquityOrDebt);
    setRePropertyTypes(props);
    setReCapitalTypes(caps);
    setRePropSeed(props);
    setReCapSeed(caps);
  }, [run]);

  if (error) {
    return <ErrorBlock message={friendlyAnthropicErrorMessage(error) ?? error} onCancel={onCancel} />;
  }
  if (!run || !run.plan_json) {
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center">
        <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Generating plan…</p>
      </div>
    );
  }

  if (run.status === "error") {
    return <ErrorBlock message={friendlyAnthropicErrorMessage(run.error_message) ?? "Plan failed"} onCancel={onCancel} />;
  }

  const plan = run.plan_json;
  const normalizedBranch = normalizeClientBranch(plan.branch);

  const tagDiff =
    normalizedBranch === "DirectEquityMezz"
      ? computeTagDiff(plan, {
          industryTypes: pickIndustryTypes,
          companyTypes: pickCompanyTypes,
          verticalTypes: pickVerticalTypes,
          stageTypes: pickStageTypes,
        })
      : {};
  const hasTagDiff = Object.keys(tagDiff).length > 0;

  const isRealEstate = normalizedBranch === "RealEstate";
  const reDirty = (() => {
    if (!isRealEstate) return false;
    if (rePropertyTypes.length !== rePropSeed.length) return true;
    for (let i = 0; i < rePropertyTypes.length; i++) {
      if (rePropertyTypes[i] !== rePropSeed[i]) return true;
    }
    if (reCapitalTypes.length !== reCapSeed.length) return true;
    for (let i = 0; i < reCapitalTypes.length; i++) {
      if (reCapitalTypes[i] !== reCapSeed[i]) return true;
    }
    return false;
  })();

  async function handleRun(skipNameCheck = false) {
    if (!gateStartRun()) return;
    // Renaming this deal to a name another existing deal already uses:
    // warn once, never block. Keeping the current name is not a collision.
    const proposed = dealName.trim();
    if (!skipNameCheck && proposed && proposed !== storedDealName.trim()) {
      try {
        const res = await checkDealNameCollision({
          data: { name: proposed, excludeDealId: run?.deal_id ?? null },
        });
        if (res.collision) {
          setNameCollision(res as DealNameCollision);
          return;
        }
      } catch {
        // Advisory only.
      }
    }
    // If the parser flagged the input as insufficient (placeholder / not a
    // real deal), surface a confirm dialog before spending credits. Older
    // plans without this field are treated as "no signal" — no gate.
    if (plan?.materialsSufficient === false) {
      setConfirmOpen(true);
      return;
    }
    await runNow();
  }

  async function runNow() {
    // [prepipeline_client:click_run] — end of D (dwell), start of E
    // (startPipeline serverFn round-trip).
    // eslint-disable-next-line no-console
    console.log("prepipeline_client", "click_run", {
      t_ms: performance.now(),
      ts_iso: new Date().toISOString(),
      runId,
    });
    const trimmed = dealName.trim();
    if (trimmed.length < 1 || trimmed.length > 100) {
      setError("Deal name is required (1–100 characters)");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Persist any typed but not yet added excluded domains before the
      // delivery starts, so matching reads the complete list.
      await planExcludedRef.current?.flushPending();
      if (run?.deal_id && trimmed !== storedDealName.trim()) {
        await renameDeal({ data: { dealId: run.deal_id, name: trimmed } });
        onDealName?.(trimmed);
      }
      // Fund branch: persist the LP-target selection BEFORE starting the
      // pipeline so Stage 2 reads the user's authoritative choices.
      if (normalizedBranch === "Fund" && lpTargets && lpTargets.length > 0) {
        await updateLpTargetsFn({ data: { runId, lpTargets } });
      }
      if (normalizedBranch === "Loans/Debt" && loanPickKind && loanPrimary) {
        await updateLoanTargetsFn({
          data: {
            runId,
            loanTypePickKind: loanPickKind,
            loanTypePrimary: loanPrimary,
            loanTargets: loanChecked,
          },
        });
      }
      // RealEstate branch: persist property/capital targeting before pipeline.
      if (isRealEstate && reDirty) {
        await updateReTargetsFn({
          data: {
            runId,
            realEstatePropertyTypes: rePropertyTypes,
            realEstateEquityOrDebt: reCapitalTypes,
          },
        });
      }
      // Hard guard: never start a Loans/Debt run with empty targeting.
      if (
        normalizedBranch === "Loans/Debt" &&
        (!loanPrimary || !loanChecked || loanChecked.length === 0)
      ) {
        setError(
          "Select a loan type before starting this run. The loan-type targeting picker above must have at least one entry checked.",
        );
        setBusy(false);
        return;
      }
      await startPipelineFn({ data: { runId } });
      onRun();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to start");
      setBusy(false);
    }
  }

  async function handleAdjust() {
    const hasFeedback = feedback.trim().length > 0;
    if (!hasFeedback && !hasTagDiff && !reDirty) return;
    setBusy(true);
    setError(null);
    try {
      // Step 1: LLM feedback pass first (if any).
      if (hasFeedback) {
        await adjustPlan({ data: { runId, feedback } });
      }
      // Step 2: tag write LAST so manual picks overwrite anything the LLM
      // changed in those four fields.
      if (hasTagDiff) {
        await updatePlanTags({ data: { runId, ...tagDiff } });
      }
      // RealEstate: targeting write LAST so manual picks win.
      if (isRealEstate && reDirty) {
        await updateReTargetsFn({
          data: {
            runId,
            realEstatePropertyTypes: rePropertyTypes,
            realEstateEquityOrDebt: reCapitalTypes,
          },
        });
      }
      // Refresh: always re-read so the displayed plan equals the persisted
      // plan; avoids assuming updatePlanTags's return shape.
      const r = (await getRun({ data: { runId } })) as RunRow;
      setRun(r);
      setFeedback("");
      setAdjusting(false);
      // Re-seed pickers from the refreshed plan on next effect tick.
      tagInitedRef.current = false;
      reInitedRef.current = false;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Adjust failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      {plan.investorTypeFallback && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>Investor type was unclear from the materials - please use Adjust to clarify.</span>
        </div>
      )}

      <section>
        <label htmlFor="deal-name" className="mb-1.5 block text-sm font-medium">
          Deal name
        </label>
        <input
          id="deal-name"
          type="text"
          value={dealName}
          maxLength={100}
          onChange={(e) => setDealName(e.target.value)}
          placeholder="e.g., ARS Aviation Q3"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] leading-[1.6]"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          1–100 characters. You can rename later from the deal page.
        </p>
      </section>

      <RaiseSummary
        plan={plan}
        headingNode={
          <Coachmark
            open={tourActive && tourStep === 1}
            title="This is what we read from your materials."
            body="Check it. If anything is off, Adjust sends you back to fix it before we spend a credit."
            step={{ current: 1, total: 3 }}
            primaryLabel="Next"
            onPrimary={() => setTourStep(2)}
            onDismiss={dismissTour}
          >
            <h2 className="mb-2 text-base font-semibold">
              Here's what we understood about your raise:
            </h2>
          </Coachmark>
        }
      />

      {Array.isArray((plan as { investor_hq_location?: unknown }).investor_hq_location) &&
        ((plan as { investor_hq_location?: string[] }).investor_hq_location ?? []).length > 0 && (
          <p className="text-sm text-foreground">
            Investor HQ locations:{" "}
            {((plan as { investor_hq_location?: string[] }).investor_hq_location ?? []).join(", ")}
          </p>
        )}

      {Number(run?.delivery_target) > 0 && (
        <div className="flex flex-wrap gap-2">
          <Stat label="Requested investors" value={String(run.delivery_target)} />
        </div>
      )}

      <section>

        <Coachmark
          open={tourActive && tourStep === 2}
          title="This is the plan."
          body="We match investors against your deal, then draft outreach in your voice for each one."
          step={{ current: 2, total: 3 }}
          primaryLabel="Next"
          onPrimary={() => setTourStep(3)}
          onDismiss={dismissTour}
        >
          <h2 className="mb-2 text-base font-semibold">Here's what we'll do:</h2>
        </Coachmark>
        <ul className="space-y-1.5 text-sm">
          <li>
            • We will find <strong>{plan.investorTypePhrase}</strong>
            {plan.typeFilterSummary && (
              <>
                , restricted to <strong>{plan.typeFilterSummary}</strong>
              </>
            )}
          </li>
          <li>• We will identify the best contact at each investor</li>
          <li>
            • We will draft a personalized email and LinkedIn message for each contact in{" "}
            <strong>{plan.voiceDescriptor}</strong>
          </li>
          <li>
            • You will receive a spreadsheet with all of this in about{" "}
            <strong>
              {(() => {
                const t = run?.delivery_target ?? 100;
                const { lo, hi } = estimateRunMinutes(t);
                return `~${lo}–${hi} minutes`;
              })()}
            </strong>
          </li>
        </ul>
      </section>

      <ParsedFromMaterials plan={plan} onReupload={onCancel} />

      {normalizedBranch === "Fund" && lpTargets && (
        <LpTargetSelector value={lpTargets} onChange={setLpTargets} />
      )}

      {normalizedBranch === "Loans/Debt" && (
        <>
          <LoanTargetingLadder
            checked={loanChecked}
            onChange={setLoanChecked}
            onReset={() => {
              if (loanSeed.length > 0) setLoanChecked(loanSeed);
            }}
            canReset={
              loanSeed.length > 0 &&
              (loanSeed.length !== loanChecked.length ||
                loanSeed.some((l, i) => l !== loanChecked[i]))
            }
          />
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {friendlyAnthropicErrorMessage(error)}
            </div>
          )}
          {adjusting ? (
            <div className="space-y-3 rounded-md border border-border p-4">
              <label className="block text-sm font-medium">What should we change?</label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] leading-[1.6]"
                placeholder="e.g., Focus on growth equity, not venture. We're a Series C, not seed."
              />
              <div className="flex gap-2">
                <button
                  disabled={busy || !feedback.trim()}
                  onClick={handleAdjust}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  {busy ? "Updating…" : "Update Plan"}
                </button>
                <button
                  onClick={() => {
                    setAdjusting(false);
                    setFeedback("");
                  }}
                  className="rounded-md border border-input px-4 py-2 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 pt-2">
              <Coachmark
                open={tourActive && tourStep === 3}
                title="Running spends credits."
                body="One credit per matched investor. Your first run is free."
                step={{ current: 3, total: 3 }}
                primaryLabel="Got it"
                onPrimary={dismissTour}
                onDismiss={dismissTour}
              >
                <button
                  disabled={busy}
                  onClick={() => void handleRun()}
                  className="rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  Run It
                </button>
              </Coachmark>
              <button
                disabled={busy}
                onClick={() => setAdjusting(true)}
                className="rounded-md border border-input bg-background px-5 py-2.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
              >
                Adjust
              </button>
              <button
                disabled={busy}
                onClick={onCancel}
                className="text-sm text-muted-foreground underline hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            This is the targeting we will run. Click chips below to add or remove loan types &mdash; the ladder is your curated list.
          </p>
          <LoanTargetSelector
            checked={loanChecked}
            onChange={setLoanChecked}
          />
        </>
      )}

      {normalizedBranch !== "Loans/Debt" && (
        <>
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {friendlyAnthropicErrorMessage(error)}
            </div>
          )}

          {adjusting ? (
            <div className="space-y-3 rounded-md border border-border p-4">
              <div className="flex justify-end">
                <button
                  disabled={busy || (!feedback.trim() && !hasTagDiff && !reDirty)}
                  onClick={handleAdjust}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  {busy ? "Updating…" : "Update Plan"}
                </button>
              </div>
              {normalizedBranch === "DirectEquityMezz" && (
                <>
                  <TagPicker label="Industry Types" value={pickIndustryTypes} options={vocab?.industryTypes ?? []} onChange={setPickIndustryTypes} />
                  <TagPicker label="Company Types" value={pickCompanyTypes} options={vocab?.companyTypes ?? []} onChange={setPickCompanyTypes} />
                  <TagPicker label="Vertical Types" value={pickVerticalTypes} options={vocab?.verticalTypes ?? []} onChange={setPickVerticalTypes} />
                  <TagPicker label="Stage Types" value={pickStageTypes} options={vocab?.stageTypes ?? []} onChange={setPickStageTypes} />
                </>
              )}
              {isRealEstate && (
                <ReTargetSelector
                  propertyTypes={rePropertyTypes}
                  onPropertyTypesChange={setRePropertyTypes}
                  propertyVocab={vocab?.realEstatePropertyTypes ?? []}
                  capitalTypes={reCapitalTypes}
                  onCapitalTypesChange={setReCapitalTypes}
                />
              )}
              <label className="block text-sm font-medium">What should we change?</label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] leading-[1.6]"
                placeholder="e.g., Focus on growth equity, not venture. We're a Series C, not seed."
              />
              <div className="flex gap-2">
                <button
                  disabled={busy || (!feedback.trim() && !hasTagDiff && !reDirty)}
                  onClick={handleAdjust}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  {busy ? "Updating…" : "Update Plan"}
                </button>
                <button
                  onClick={() => {
                    setAdjusting(false);
                    setFeedback("");
                  }}
                  className="rounded-md border border-input px-4 py-2 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 pt-2">
              <Coachmark
                open={tourActive && tourStep === 3}
                title="Running spends credits."
                body="One credit per matched investor. Your first run is free."
                step={{ current: 3, total: 3 }}
                primaryLabel="Got it"
                onPrimary={dismissTour}
                onDismiss={dismissTour}
              >
                <button
                  disabled={busy}
                  onClick={() => void handleRun()}
                  className="rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  Run It
                </button>
              </Coachmark>
              <button
                disabled={busy}
                onClick={() => setAdjusting(true)}
                className="rounded-md border border-input bg-background px-5 py-2.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
              >
                Adjust
              </button>
              <button
                disabled={busy}
                onClick={onCancel}
                className="text-sm text-muted-foreground underline hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          )}
        </>
      )}
      {run?.deal_id && (
        <DealExcludedDomains
          ref={planExcludedRef}
          dealId={run.deal_id as string}
          collapsible
          title="Excluded firms (optional)"
        />
      )}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run anyway?</AlertDialogTitle>
            <AlertDialogDescription>
              {plan.insufficientReason ??
                "This deal doesn't have enough detail to produce useful matches."}
              {" "}
              Running now will use approximately {Number(run?.delivery_target) || 0} credit
              {Number(run?.delivery_target) === 1 ? "" : "s"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                setConfirmOpen(false);
                void runNow();
              }}
            >
              Run
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <DealNameCollisionDialog
        collision={nameCollision}
        onOpenChange={(open) => {
          if (!open) setNameCollision(null);
        }}
        onContinue={() => {
          setNameCollision(null);
          void handleRun(true);
        }}
      />
    </div>
  );
}

/* ---------------- Running View ---------------- */

/**
 * Display steps for the progress list. These are a UI-only decomposition of
 * the four backend stages: "Setting up" and "Matching investors" are both
 * sub-states of backend Stage 1 rendered as two rows so first-time watchers
 * see the transition from "kicking off" to "counting matches". The three
 * remaining rows map 1:1 to backend Stages 2/3/4. State is derived in
 * RunningView, not baked in here.
 */
type DisplayStepId = "setup" | "match" | "identify" | "draft" | "build";
const DISPLAY_STEPS: ReadonlyArray<{ id: DisplayStepId; backendStage: number }> = [
  { id: "setup", backendStage: 1 },
  { id: "match", backendStage: 1 },
  { id: "identify", backendStage: 2 },
  { id: "draft", backendStage: 3 },
  { id: "build", backendStage: 4 },
];

/**
 * Monotonic count-up animation for the active stage 1 investor count.
 * - Only ever animates upward; never decreases.
 * - Reaches the target within ~500ms so it never lags behind the pipeline.
 * - When `snap` flips true (stage ends), jumps immediately to the latest target.
 * Pure display layer: consumes values already polled by the parent.
 */
function AnimatedCount({ target, snap }: { target: number; snap: boolean }) {
  // Start at 0 so first-time watchers see the count fill from empty rather
  // than appearing partway. Monotonic upward from there.
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  const toRef = useRef(0);
  const startRef = useRef(0);
  const durationRef = useRef(2000);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Monotonic: ignore decreases.
    const next = Math.max(toRef.current, target);
    if (snap) {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      toRef.current = next;
      fromRef.current = next;
      setDisplay(next);
      return;
    }
    if (next === toRef.current && display === next) return;
    fromRef.current = display;
    toRef.current = next;
    startRef.current = performance.now();
    // Smooth continuous fill for the first-time watcher. Each unit is
    // paced at ~120ms so small chunky backend writes (e.g. 20→30→34→39
    // →73) ease into a continuous climb rather than lurching. Floor at
    // 1800ms so even a single-unit tick reads as motion; cap at 12s so a
    // large supplement jump (e.g. curated 48 → universe 148) still lands
    // within the identify step's wall time.
    const delta = Math.max(0, toRef.current - fromRef.current);
    durationRef.current = Math.min(12000, Math.max(1800, delta * 120));
    const tick = (now: number) => {
      const t = Math.min(1, (now - startRef.current) / durationRef.current);
      // easeOutCubic
      const e = 1 - Math.pow(1 - t, 3);
      const v = Math.round(fromRef.current + (toRef.current - fromRef.current) * e);
      setDisplay(v);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, snap]);

  return <span className="tabular-nums">{display}</span>;
}

function RunningView({
  runId,
  onComplete,
  onNeedsRefinement,
  onStartOver,
}: {
  runId: string;
  onComplete: () => void;
  onNeedsRefinement: () => void;
  onStartOver: () => void;
}) {
  const [run, setRun] = useState<RunRow | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryingStage2, setRetryingStage2] = useState(false);
  const startTimeRef = useRef<number>(Date.now());
  // 1-second re-render tick so the elapsed timer updates smoothly instead
  // of only advancing on the 2s poll cadence.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  // Tracks when we first observed status === "error" in this polling session.
  // The backend's recovery loop often flips a run from running → error →
  // running → complete as it absorbs transient stage failures (e.g. the
  // "Invalid typed array length" path). We only treat an error as terminal
  // once it has persisted across the confirmation window below; otherwise
  // the UI would flash a misleading failure banner mid-run.
  const errorFirstSeenAtRef = useRef<number | null>(null);
  const [errorConfirmed, setErrorConfirmed] = useState(false);
  // Backend stage retries can briefly leave runs.status = 'error' in the
  // DB before recovery flips it back to 'running'. The H-fix keeps the
  // poller alive across error states, so the banner self-clears on real
  // recovery; this short window just suppresses the flash. The Swan Valley
  // recovery took ~13min - far longer than any sane grace window would
  // cover - so widening this further would only mask real signal. Root
  // cause (concurrent pipeline entry) is being fixed at the gate, not here.
  const ERROR_CONFIRM_MS = 15000;
  // Synchronous one-shot locks - useState updates are async, so two clicks
  // fired in the same tick (React StrictMode double-invoke, real human
  // double-tap) can both pass an `if (retrying)` guard before state flips.
  // The lock helper flips synchronously and blocks the second invocation cold.
  // Tested in tests/retry-debounce.test.ts.
  const retryLockRef = useRef(createRetryLock());
  const retryStage2LockRef = useRef(createRetryLock());
  const stage1MaxRef = useRef(0);
  const stage1StableSinceRef = useRef<number>(Date.now());
  // Monotonic non-decreasing displayed match count. Reflects the REAL
  // matched universe (Philosophy B): the number of investor firms Stage 1
  // returned. Intentionally uncapped by delivery_target — matched can and
  // often does exceed the number we ultimately deliver.
  const matchDisplayRef = useRef<number>(0);
  // Once the match step evaluates as done for the first time, latch true
  // permanently. Guarantees the step never un-latches even if a later
  // stage rewrites investor_results, restarts the stability window (as
  // the RealEstate supplement merge does), or the poll briefly reports a
  // regressed backend stage. `isDone` reads this ref directly and we
  // never write `false` back to it anywhere in this component.
  //
  // Persisted to sessionStorage keyed by runId so a RunningView
  // unmount+remount (view flip, error boundary, RealEstate supplement
  // path) rehydrates the latch instead of resetting to false and
  // re-showing the match spinner. Stale entries from other runIds are
  // ignored/cleaned on read so a fresh run never inherits "done".
  const MATCH_LATCH_PREFIX = "cd:match-latch:";
  const matchLatchKey = `${MATCH_LATCH_PREFIX}${runId}`;
  const readMatchLatch = (): { done: boolean; matchedFinal: number | null } => {
    if (typeof window === "undefined") return { done: false, matchedFinal: null };
    try {
      // Clean stale entries from prior runs in this tab.
      for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
        const k = window.sessionStorage.key(i);
        if (k && k.startsWith(MATCH_LATCH_PREFIX) && k !== matchLatchKey) {
          window.sessionStorage.removeItem(k);
        }
      }
      const raw = window.sessionStorage.getItem(matchLatchKey);
      if (!raw) return { done: false, matchedFinal: null };
      const parsed = JSON.parse(raw) as { done?: boolean; matchedFinal?: number | null };
      return {
        done: parsed.done === true,
        matchedFinal:
          typeof parsed.matchedFinal === "number" ? parsed.matchedFinal : null,
      };
    } catch {
      return { done: false, matchedFinal: null };
    }
  };
  const initialLatch = useRef(readMatchLatch());
  const matchDoneStickyRef = useRef<boolean>(initialLatch.current.done);
  // The matched count captured at the exact tick the match step latched
  // done. Once frozen, the "Matched N investors" label reads from this
  // ref and cannot move backward if later polls rewrite
  // investor_results.
  const matchedFinalRef = useRef<number | null>(initialLatch.current.matchedFinal);
  const writeMatchLatch = (matchedFinal: number | null) => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(
        matchLatchKey,
        JSON.stringify({ done: true, matchedFinal }),
      );
    } catch {
      /* storage full / disabled — non-fatal */
    }
  };

  // Instrumentation: log RunningView mount/unmount and runId changes so
  // we can identify what causes the remount that motivated the
  // sessionStorage-backed latch. Console-only, no user-visible effect.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // eslint-disable-next-line no-console
    console.log("[RunningView] mount", {
      runId,
      rehydratedLatch: initialLatch.current,
      t: new Date().toISOString(),
    });
    return () => {
      // eslint-disable-next-line no-console
      console.log("[RunningView] unmount", {
        runId,
        matchDoneAtUnmount: matchDoneStickyRef.current,
        matchedFinalAtUnmount: matchedFinalRef.current,
        t: new Date().toISOString(),
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const queryClient = useQueryClient();

  // Clear the persisted latch on terminal statuses so the sessionStorage
  // entry does not linger for this runId beyond its useful life.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (
      run &&
      (run.status === "complete" ||
        run.status === "error" ||
        run.status === "canceled" ||
        run.status === "needs_refinement")
    ) {
      try {
        window.sessionStorage.removeItem(matchLatchKey);
      } catch {
        /* noop */
      }
      queryClient.invalidateQueries({ queryKey: ["my-recent-deals"] });
      // Refresh a deal-detail view that may be open in another tab so its
      // in-flight banner clears and its contact list picks up the newly
      // delivered rows without a manual reload.
      if (run.deal_id) {
        queryClient.invalidateQueries({ queryKey: ["deal-detail", run.deal_id] });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.status, runId]);

  // Quiet URL resume: stamp ?runId=<id> so a refresh or accidental tab
  // close-and-reopen lands back in this running view via the existing
  // deep-link loader in OutreachBuilder. Uses replaceState so it does not
  // pollute browser history.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("runId") === runId) return;
    params.set("runId", runId);
    params.delete("dealId");
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
    );
  }, [runId]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    async function tick() {
      try {
        const r = (await getRun({ data: { runId } })) as RunRow;
        if (cancelled) return;
        setRun(r);
        if (r.status === "complete") {
          errorFirstSeenAtRef.current = null;
          setErrorConfirmed(false);
          queryClient.invalidateQueries({ queryKey: ["my-credits"] });
          if (typeof window !== "undefined") {
            const p = new URLSearchParams(window.location.search);
            if (p.has("runId")) {
              p.delete("runId");
              const qs = p.toString();
              window.history.replaceState(
                null,
                "",
                qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
              );
            }
          }
          onComplete();
          return;
        }
        if (r.status === "needs_refinement") {
          errorFirstSeenAtRef.current = null;
          setErrorConfirmed(false);
          queryClient.invalidateQueries({ queryKey: ["my-credits"] });
          onNeedsRefinement();
          return;
        }
        if (r.status === "canceled") {
          // Terminal - stop polling. UI renders the canceled state.
          errorFirstSeenAtRef.current = null;
          setErrorConfirmed(false);
          return;
        }
        if (r.status === "error") {
          // Keep polling - recovery may flip status back to running/complete.
          if (errorFirstSeenAtRef.current === null) {
            errorFirstSeenAtRef.current = Date.now();
          }
          const elapsedErr = Date.now() - errorFirstSeenAtRef.current;
          if (elapsedErr >= ERROR_CONFIRM_MS) {
            setErrorConfirmed(true);
          }
          timer = setTimeout(tick, 2000);
          return;
        }
        // status === "running" (or any non-terminal): clear any prior
        // transient-error marker.
        if (errorFirstSeenAtRef.current !== null) {
          errorFirstSeenAtRef.current = null;
          setErrorConfirmed(false);
        }
        timer = setTimeout(tick, 2000);
      } catch {
        if (!cancelled) timer = setTimeout(tick, 3000);
      }
    }
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [runId, onComplete, onNeedsRefinement]);

  async function handleRetry() {
    await retryLockRef.current.run(async () => {
      setRetrying(true);
      try {
        await retryRun({
          data: { runId, fromStage: run?.error_stage ?? undefined },
        });
      } finally {
        setRetrying(false);
      }
    });
  }

  async function handleRetryFromStage2() {
    await retryStage2LockRef.current.run(async () => {
      const investorCount = run?.investor_results?.length ?? 0;
      const ok = window.confirm(
        `Restart pipeline from Stage 2 (Identifying contacts), preserving ${investorCount} Stage 1 investors?`,
      );
      if (!ok) return;
      setRetryingStage2(true);
      try {
        await retryRun({ data: { runId, fromStage: 2 } });
      } finally {
        setRetryingStage2(false);
      }
    });
  }

  if (!run) {
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
  const { lo: estLoMin, hi: estHiMin, total: estTotalMin } =
    estimateRunMinutes(run.delivery_target ?? 100);
  // Elapsed-up display: counts up continuously. When elapsed exceeds the
  // scaled estimate it simply keeps climbing (reads as "running a bit
  // long", never as a stuck 0:00 countdown).
  const estTargetMin = Math.max(1, Math.round(estTotalMin));
  const elapsedMin = Math.floor(elapsed / 60);
  const elapsedSec = elapsed % 60;

  const stage1InvestorCount = run.investor_results?.length ?? 0;
  // Match count is monotonic and UNCAPPED — this is the real Stage 1
  // universe size, which may legitimately exceed the eventual delivered
  // count. Only grow on strictly increasing values; a transient regression
  // in investor_results.length cannot pull the display backward.
  if (stage1InvestorCount > stage1MaxRef.current) {
    stage1MaxRef.current = stage1InvestorCount;
    stage1StableSinceRef.current = Date.now();
  }
  if (stage1MaxRef.current > matchDisplayRef.current) {
    matchDisplayRef.current = stage1MaxRef.current;
  }
  // Once the match step has latched done, freeze the displayed count at
  // the value captured at that moment (`matchedFinalRef`). This is what
  // guarantees "Matched N investors" locks and stays locked.
  const stage1Display =
    matchDoneStickyRef.current && matchedFinalRef.current != null
      ? matchedFinalRef.current
      : matchDisplayRef.current;
  // Delivered count = the number of top contacts actually selected for
  // outreach. Distinct from "matched" (superset) and shown as its own
  // labeled number so the two never collapse onto the same line.
  //   1. If contact_results has landed, that is the ground-truth delivered
  //      count (correct even on RealEstate where delivery_target may be null).
  //   2. Otherwise fall back to run.delivery_target (the target we asked for).
  //   3. Last resort: the matched count, so the copy still renders.
  const deliveredCount = run.contact_results?.length ?? 0;
  const rawDelivery =
    typeof run.delivery_target === "number" &&
    Number.isFinite(run.delivery_target) &&
    run.delivery_target >= 1
      ? Math.floor(run.delivery_target)
      : null;
  const deliveredDisplay =
    deliveredCount > 0
      ? deliveredCount
      : (rawDelivery ?? (matchDisplayRef.current || 0));
  const STAGE1_STABLE_MS = 15000;
  const stage1Final =
    run.status === "complete" ||
    run.current_stage >= 3 ||
    (run.current_stage >= 2 &&
      Date.now() - stage1StableSinceRef.current >= STAGE1_STABLE_MS);
  // Setup completes as soon as the pipeline is really under way: first
  // investor_results write, current_stage past 1, or terminal complete.
  const setupDone =
    (run.investor_results?.length ?? 0) > 0 ||
    run.current_stage > 1 ||
    run.status === "complete";
  // Treat the run as "in error" for UI purposes only after the error has
  // been confirmed by the polling loop above. Transient error snapshots
  // (which the backend recovers from) render as normal running state.
  const IN_FLIGHT_STATUSES = new Set(["planning", "plan_ready", "running"]);
  const isInFlight = IN_FLIGHT_STATUSES.has(run.status);
  const showErrorUI = run.status === "error" && errorConfirmed;
  const showCanceledUI = run.status === "canceled";
  const canRetryFromStage2 =
    stage1InvestorCount > 0 &&
    (showErrorUI || run.status === "running") &&
    run.current_stage >= 1 &&
    run.current_stage <= 2;
  const totalContactsLoaded = run.total_contacts_loaded ?? null;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-card p-6">
        <ul className="space-y-3">
          {DISPLAY_STEPS.map((step) => {
            // Per-step done/active flags derived from the backend stage,
            // the setup/stage1Final gates, and the confirmed-error state.
            let isDone = false;
            let isActive = false;
            if (step.id === "setup") {
              isDone = setupDone;
              isActive = !showErrorUI && isInFlight && !isDone;
            } else if (step.id === "match") {
              // Only evaluate the done-condition until we've latched; once
              // latched, we never re-derive it, so no downstream state
              // (investor_results rewrites, current_stage regressions,
              // stability-window resets) can flip the row back to active.
              if (!matchDoneStickyRef.current) {
                const computedDone =
                  stage1Final &&
                  (run.current_stage > 1 || run.status === "complete");
                if (computedDone) {
                  matchDoneStickyRef.current = true;
                  matchedFinalRef.current = matchDisplayRef.current;
                  writeMatchLatch(matchedFinalRef.current);
                }
              }
              isDone = matchDoneStickyRef.current;
              isActive = !showErrorUI && isInFlight && setupDone && !isDone;
            } else if (step.id === "identify") {
              isDone = run.current_stage > 2 || run.status === "complete";
              isActive =
                !showErrorUI &&
                isInFlight &&
                stage1Final &&
                run.current_stage === 2 &&
                !isDone;
            } else if (step.id === "draft") {
              isDone = run.current_stage > 3 || run.status === "complete";
              isActive =
                !showErrorUI && isInFlight && run.current_stage === 3 && !isDone;
            } else {
              // build
              isDone = run.status === "complete";
              isActive =
                !showErrorUI && isInFlight && run.current_stage === 4 && !isDone;
            }
            const isError = showErrorUI && run.error_stage === step.backendStage;
            // Label copy per state. Kept short; no em dashes.
            let label: ReactNode;
            if (isError) {
              const failCopy: Record<DisplayStepId, string> = {
                setup: "Setting up: failed",
                match: "Matching investors: failed",
                identify: "Identifying contacts: failed",
                draft: "Drafting outreach: failed",
                build: "Building spreadsheet: failed",
              };
              label = failCopy[step.id];
            } else if (step.id === "setup") {
              label = isDone ? "Analyzing your deal" : "Setting up…";
            } else if (step.id === "match") {
              if (isDone) {
                label = `Matched ${stage1Display} investors`;
              } else if (isActive) {
                label = (
                  <>
                    Matching investors…{" "}
                    <AnimatedCount target={stage1Display} snap={stage1Final} />
                  </>
                );
              } else {
                label = "Matching investors";
              }
            } else if (step.id === "identify") {
              if (isDone)
                label = `Selected your top ${deliveredDisplay} contacts by rating`;
              else label = "Identifying contacts…";
            } else if (step.id === "draft") {
              label = isDone ? "Drafted outreach" : "Drafting outreach…";
            } else {
              label = isDone ? "Done" : "Building spreadsheet…";
            }
            const showIdentifySubtitle =
              step.id === "identify" && isActive && totalContactsLoaded != null;
            return (
              <li key={step.id} className="flex items-start gap-3">
                <div className="mt-0.5 flex h-6 w-6 items-center justify-center">
                  {isDone ? (
                    <Check className="h-5 w-5 text-emerald-600" />
                  ) : isActive ? (
                    <Loader2 className="h-5 w-5 animate-spin text-foreground" />
                  ) : isError ? (
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  ) : (
                    <div className="mt-2 h-2 w-2 rounded-full bg-muted-foreground/30" />
                  )}
                </div>
                <div className="flex flex-col">
                  <span
                    className={
                      "text-sm " +
                      (isDone
                        ? "text-foreground"
                        : isActive
                          ? "font-medium text-foreground"
                          : "text-muted-foreground")
                    }
                  >
                    {label}
                  </span>
                  {showIdentifySubtitle && (
                    <span className="text-xs text-muted-foreground">
                      Searching {totalContactsLoaded!.toLocaleString()} investor
                  contacts to find your top {rawDelivery ?? deliveredDisplay}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {showErrorUI ? (
        <div className="space-y-3 rounded-md border border-destructive/40 bg-destructive/5 p-4">
          <div className="flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{friendlyAnthropicErrorMessage(run.error_message)}</span>
          </div>
          {(() => {
            const MAX_RETRIES = 3;
            const usedRetries =
              (run as { retry_count?: number | null }).retry_count ?? 0;
            const capReached = usedRetries >= MAX_RETRIES;
            return (
              <>
                <div className="text-xs text-muted-foreground">
                  {capReached
                    ? `Retry limit reached (${usedRetries}/${MAX_RETRIES}). Start a new run.`
                    : `Retries used: ${usedRetries}/${MAX_RETRIES}`}
                </div>
                <div className="flex flex-wrap gap-3">
                  {!capReached ? (
                    <button
                      disabled={retrying}
                      onClick={handleRetry}
                      className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                    >
                      {retrying ? "Retrying…" : `Retry from step ${run.error_stage}`}
                    </button>
                  ) : null}
                  {!capReached && canRetryFromStage2 && run.error_stage !== 2 ? (
                    <button
                      disabled={retryingStage2}
                      onClick={handleRetryFromStage2}
                      className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
                    >
                      {retryingStage2
                        ? "Restarting…"
                        : `Retry from Stage 2 (keep ${stage1InvestorCount} investors)`}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={onStartOver}
                    className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
                  >
                    Start a new run
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      ) : showCanceledUI ? (
        <div className="space-y-3 rounded-md border border-destructive/40 bg-destructive/5 p-4">
          <div className="flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{friendlyAnthropicErrorMessage(run.error_message) ?? "This run was canceled."}</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onStartOver}
              className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
            >
              Start a new run
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-center text-xs text-muted-foreground">
            Elapsed {elapsedMin}:{elapsedSec.toString().padStart(2, "0")} / ~{estTargetMin} min
            {" "}(typical run: ~{estLoMin}–{estHiMin} minutes) · Keep this tab open.
          </p>
        </div>
      )}
    </div>
  );
}

/* ---------------- Download View ---------------- */

function DownloadView({ runId, onAnother }: { runId: string; onAnother: () => void }) {
  const [run, setRun] = useState<RunRow | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    getRun({ data: { runId } }).then((r) => setRun(r as RunRow));
  }, [runId]);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      const { url } = await refreshDownload({ data: { runId } });
      if (url && run) setRun({ ...run, final_xlsx_url: url });
    } finally {
      setRefreshing(false);
    }
  }

  // Fetch the xlsx as a blob and trigger download with the deal-named filename.
  // Necessary because the `download` attribute is ignored for cross-origin
  // (Supabase storage) URLs, so the browser would otherwise use the
  // server-side path filename.
  async function handleDownload() {
    if (!run?.final_xlsx_url) return;
    const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const filename = `${slugifyDealName(run.deal_name)}_${date}.xlsx`;
    setDownloading(true);
    try {
      const res = await fetch(run.final_xlsx_url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      // Fallback: open the signed URL directly (browser will save with
      // server-side filename).
      window.open(run.final_xlsx_url, "_blank", "noopener");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <CompletionView
      run={run}
      runId={runId}
      downloading={downloading}
      refreshing={refreshing}
      showBreakdown={showBreakdown}
      onToggleBreakdown={() => setShowBreakdown((v) => !v)}
      onDownload={handleDownload}
      onRefresh={handleRefresh}
      onAnother={onAnother}
    />
  );
}

function CompletionView({
  run,
  runId,
  downloading,
  refreshing,
  showBreakdown,
  onToggleBreakdown,
  onDownload,
  onRefresh,
  onAnother,
}: {
  run: RunRow | null;
  runId: string;
  downloading: boolean;
  refreshing: boolean;
  showBreakdown: boolean;
  onToggleBreakdown: () => void;
  onDownload: () => void;
  onRefresh: () => void;
  onAnother: () => void;
}) {
  const fetchSummary = useServerFn(getRunCompletionSummary);
  const [pollStartedAt] = useState(() => Date.now());
  const summary = useQuery({
    queryKey: ["run-completion-summary", runId],
    queryFn: () => fetchSummary({ data: { runId } }),
    staleTime: 60_000,
    refetchInterval: (q) => {
      const d = q.state.data as
        | { stats?: { contacts_total?: number } }
        | undefined;
      if (!d) return 2000;
      const total = d.stats?.contacts_total ?? 0;
      if (total === 0 && Date.now() - pollStartedAt < 90_000) return 3000;
      return false;
    },
  });

  // Hold the loader until aggregate counts have actually populated. The
  // run row flips to "complete" before deal_contacts finish materializing,
  // so the summary query can briefly return all zeros. We poll until we
  // see a non-zero contacts_total or hit the 90s safety cutoff.
  const totalContacts = summary.data?.stats.contacts_total ?? 0;
  const stillPopulating =
    !!summary.data &&
    totalContacts === 0 &&
    Date.now() - pollStartedAt < 90_000;
  if (
    !run ||
    summary.isLoading ||
    !summary.data ||
    !summary.data.deal_id ||
    stillPopulating
  ) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { stats, readiness, is_first_run, deal_id } = summary.data;
  // Type taxonomy strings from the directory sometimes arrive as a raw
  // multi-segment value joined by a literal `\N` delimiter, e.g.
  // "ASSET MANAGER (PRIMARY TYPE)\NINFRASTRUCTURE\N...". Show only the
  // first clean segment.
  const cleanCategory = (raw: string): string => {
    const first = raw.split(/\\N/)[0] ?? raw;
    return (
      first.replace(/\s*\(PRIMARY TYPE\)\s*/i, "").trim() || "Unspecified"
    );
  };
  const sortedTypes = Object.entries(stats.type_breakdown)
    .filter(([, n]) => n > 0)
    .map(([t, n]) => [cleanCategory(t), n] as [string, number])
    // The "Other" bucket is kept and relabeled as "Unverified" downstream
    // so unclassified delivered firms remain visible in the breakdown.
    .sort((a, b) => {
      const aFo = /family\s*office/i.test(a[0]) ? 1 : 0;
      const bFo = /family\s*office/i.test(b[0]) ? 1 : 0;
      if (aFo !== bFo) return bFo - aFo;
      return b[1] - a[1];
    })
    .slice(0, 4);

  const exportAvailable = Boolean(run.final_xlsx_url);

  return (
    <div className="space-y-8">
      {/* 1. Value summary */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-emerald-700">
          <Check className="h-5 w-5" />
          <span className="text-sm font-medium uppercase tracking-wide">
            Your outreach is ready
          </span>
        </div>
        <h2 className="font-serif text-3xl tracking-tight text-foreground">
          Your matches for {run.deal_name ?? "this deal"} are ready to review
          and contact.
        </h2>
        {(() => {
          const sf = run.plan_json?.typeFilterShortfall;
          if (!sf || sf.delivered >= sf.requested) return null;
          return (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              We found <strong>{sf.delivered}</strong> of your requested{" "}
              <strong>{sf.requested}</strong> investors matching your investor-type
              restriction. To get more, broaden the investor types or remove the
              restriction.
            </div>
          );
        })()}
        <div className="flex flex-wrap gap-2 pt-1">
          {(() => {
            const isFund =
              (stats as { branch?: string | null }).branch === "Fund";
            // Delivered-count by investor type. Sourced from the same
            // "Investor Type" column the spreadsheet uses (directory
            // tier1 [/ tier2] on RE, canonical firm type elsewhere).
            // Unclassified firms roll up to the "Other" bucket via
            // rollUpInvestorType — relabel that as "Unverified" here so
            // the tile row states clearly which delivered firms have no
            // directory classification.
            const typeTiles = sortedTypes
              .filter(([t]) => !/family\s*office/i.test(t))
              .slice(0, 3)
              .map(([t, n]) => [t === "Other" ? "Unverified" : t, n] as [string, number]);
            return (
              <>
                {stats.investors_total > 0 && (
                  <Stat
                    label="Investors matched"
                    value={String(stats.investors_total)}
                  />
                )}
                {stats.contacts_total > 0 && (
                  <Stat label="Contacts" value={String(stats.contacts_total)} />
                )}
                {Number(run.delivery_target) > 0 && (
                  <Stat label="Requested" value={String(run.delivery_target)} />
                )}
                {stats.rated_80_plus > 0 && !isFund && (
                  <Stat label="Rated 80+" value={String(stats.rated_80_plus)} />
                )}
                {stats.family_office_count > 0 && (
                  <Stat
                    label="Family offices"
                    value={String(stats.family_office_count)}
                    emphasis
                  />
                )}
                {typeTiles.map(([t, n]) => (
                  <Stat key={t} label={t} value={String(n)} />
                ))}
              </>
            );
          })()}
        </div>
      </section>

      {/* 2. Readiness report highlights */}
      {readiness && (readiness.strengths.length > 0 || readiness.lead_profile) && (
        <section className="space-y-4 rounded-xl border border-border bg-muted/20 p-5">
          <div className="flex items-baseline justify-between gap-4">
            <div>
              <h3 className="font-serif text-xl text-foreground">
                Investor Readiness Report
              </h3>
              {readiness.score != null && (
                <p className="text-xs text-muted-foreground">
                  Readiness score {readiness.score}/100
                </p>
              )}
            </div>
            <Link
              to="/deals/$dealId/deck-studio"
              params={{ dealId: deal_id }}
              className="text-sm font-medium text-primary no-underline hover:underline"
            >
              View full report →
            </Link>
          </div>

          {readiness.strengths.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Key strengths
              </h4>
              <div className="space-y-2.5">
                {readiness.strengths.map((s, i) => (
                  <div key={i} className="rounded-md border border-border bg-background p-3">
                    <p className="text-sm font-medium text-foreground">{s.strength}</p>
                    {s.how_to_leverage && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {s.how_to_leverage}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {readiness.lead_profile?.required_profile && (
            <div className="space-y-1.5">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Lead investor profile
              </h4>
              <p className="text-sm text-foreground">
                {readiness.lead_profile.required_profile}
              </p>
              {readiness.lead_profile.why && (
                <p className="text-xs text-muted-foreground">
                  {readiness.lead_profile.why}
                </p>
              )}
            </div>
          )}
        </section>
      )}

      {/* 3. Primary CTA */}
      <section className="space-y-2">
        <Link
          to="/deals/$dealId/contacts"
          params={{ dealId: deal_id }}
          onClick={() =>
            track(
              "completion_cta_clicked",
              { target: "work_list", is_first_run },
              { deal_id, report_id: runId },
            )
          }
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-4 text-base font-semibold text-primary-foreground no-underline shadow hover:bg-primary/90 sm:w-auto"
        >
          Go to your investor contact list →
        </Link>
        <p className="text-xs text-muted-foreground">
          Opens your Investors &amp; Contacts, where you can contact them with
          bespoke messages, take notes, and track their status.
        </p>
      </section>

      {/* 5. Export, demoted */}
      <section className="flex flex-wrap items-center gap-3 border-t border-border pt-5 text-sm">
        {exportAvailable ? (
          <button
            type="button"
            onClick={() => {
              track(
                "completion_cta_clicked",
                { target: "export", is_first_run },
                { deal_id, report_id: runId },
              );
              onDownload();
            }}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-60"
          >
            <Download className="h-3.5 w-3.5" />
            {downloading ? "Preparing…" : "Export to spreadsheet"}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">Preparing export…</span>
        )}
        <button
          onClick={onRefresh}
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          {refreshing ? "Refreshing…" : "Get a new download link"}
        </button>
        <Link
          to="/dashboard"
          className="text-xs text-muted-foreground no-underline hover:text-foreground"
        >
          Back to dashboard
        </Link>
        <button
          type="button"
          onClick={onAnother}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Start another deal
        </button>
        <div className="ml-auto flex items-center gap-2">
          <RunCost run={run} expanded={showBreakdown} onToggle={onToggleBreakdown} />
        </div>
      </section>
      {showBreakdown && <CostBreakdown run={run} />}
    </div>
  );
}

function Stat({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div
      className={
        emphasis
          ? "rounded-lg border border-primary/30 bg-primary/5 px-3 py-2"
          : "rounded-lg border border-border bg-background px-3 py-2"
      }
    >
      <div className="text-xl font-semibold leading-tight text-foreground">{value}</div>
      <div className="text-[12px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function formatUsd(n: number, digits = 2): string {
  return `$${n.toFixed(digits)}`;
}

function RunCost({
  run,
  expanded,
  onToggle,
}: {
  run: RunRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  const cost = Number(run.cost_usd ?? 0);
  return (
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-[13.5px] text-muted-foreground hover:bg-accent"
      title="Click for per-stage breakdown"
    >
      {expanded ? (
        <ChevronDown className="h-3.5 w-3.5" />
      ) : (
        <ChevronRight className="h-3.5 w-3.5" />
      )}
      <span>
        Cost: <span className="font-medium text-foreground">{formatUsd(cost)}</span>
      </span>
    </button>
  );
}

function CostBreakdown({ run }: { run: RunRow }) {
  const sc = run.stage_costs ?? {};
  const sum = (...keys: string[]) => keys.reduce((t, k) => t + (sc[k]?.cost ?? 0), 0);
  const planCost = sum("stage_0_plan");
  const investorsCost = sum("stage_1");
  const contactsCost = sum("stage_2");
  const outreachCost = sum("stage_3");
  const parts = [
    ["Plan", planCost],
    ["Investors", investorsCost],
    ["Contacts", contactsCost],
    ["Outreach", outreachCost],
  ] as const;
  return (
    <div className="rounded-md border border-border bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
      {parts.map(([label, c], i) => (
        <span key={label}>
          {i > 0 && <span className="mx-1.5">·</span>}
          {label}: <span className="font-medium text-foreground">{formatUsd(c, 2)}</span>
        </span>
      ))}
    </div>
  );
}

function ErrorBlock({ message, onCancel }: { message: string; onCancel: () => void }) {
  return (
    <div className="space-y-4 rounded-md border border-destructive/40 bg-destructive/5 p-6">
      <div className="flex items-start gap-2 text-sm text-destructive">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <span>{message}</span>
      </div>
      <button
        onClick={onCancel}
        className="rounded-md border border-input bg-background px-4 py-2 text-sm"
      >
        Start over
      </button>
    </div>
  );
}
/* ---------------- Refine View ---------------- */

function RefineView({
  runId,
  onContinue,
  onCancel,
}: {
  runId: string;
  onContinue: () => void;
  onCancel: () => void;
}) {
  const [run, setRun] = useState<RunRow | null>(null);
  const [phraseChoice, setPhraseChoice] = useState<string>("__ai__");
  const [investorTypesRequest, setInvestorTypesRequest] = useState<string>("");
  const [typesEdited, setTypesEdited] = useState(false);
  const [additional, setAdditional] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getRun({ data: { runId } }).then((r) => {
      setRun(r as RunRow);
      const current = (r as RunRow).plan_json?.investorTypePhrase ?? "";
      if ((VALID_INVESTOR_PHRASES as readonly string[]).includes(current)) {
        setPhraseChoice(current);
      }
      // Extract investor_types_request from deal_context marker block so
      // the user can see + edit what they originally asked for.
      const ctx = (r as { deal_context?: string | null }).deal_context ?? "";
      const m = ctx.match(/--- investor types requested ---\n([\s\S]*?)(?=\n\n---|\n*$)/);
      setInvestorTypesRequest(m ? m[1].trim() : "");
    });
  }, [runId]);

  if (!run || !run.plan_json) {
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const plan = run.plan_json;

  async function handleRefine() {
    setBusy(true);
    setError(null);
    try {
      await refineRun({
        data: {
          runId,
          investorTypePhraseOverride: phraseChoice === "__ai__" ? null : phraseChoice,
          // Only send when the user actually touched the field - undefined
          // tells the server to leave the marker block alone.
          investorTypesRequestOverride: typesEdited ? investorTypesRequest : undefined,
          additionalContext: additional,
        },
      });
      onContinue();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Refine failed");
      setBusy(false);
    }
  }

  async function handleProceedAnyway() {
    setBusy(true);
    setError(null);
    try {
      await refineRun({
        data: {
          runId,
          proceedAnyway: true,
        },
      });
      onContinue();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to continue");
      setBusy(false);
    }
  }

  async function handleCancel() {
    setBusy(true);
    try {
      await cancelRun({ data: { runId } });
    } finally {
      onCancel();
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-medium">We need to refine the deal before continuing</p>
            <p className="mt-1">{friendlyAnthropicErrorMessage(run.error_message)}</p>
          </div>
        </div>
      </div>

      <section>
        <h2 className="mb-2 text-base font-semibold">What we understood:</h2>
        <p className="text-sm leading-relaxed">{plan.dealSummary}</p>
        <p className="mt-2 text-sm">
          Currently targeting: <strong>{plan.investorTypePhrase}</strong>
        </p>
      </section>

      <div>
        <label className="mb-1.5 block text-sm font-medium">Override investor type</label>
        <select
          value={phraseChoice}
          onChange={(e) => setPhraseChoice(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] leading-[1.6]"
        >
          <option value="__ai__">Use AI's recommendation ({plan.investorTypePhrase})</option>
          {VALID_INVESTOR_PHRASES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium">
          Investor types requested
        </label>
        <textarea
          value={investorTypesRequest}
          onChange={(e) => {
            setInvestorTypesRequest(e.target.value.slice(0, 500));
            setTypesEdited(true);
          }}
          rows={2}
          maxLength={500}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] leading-[1.6]"
          placeholder='e.g., growth equity, single family offices. Or "all except SPACs". Blank means "All".'
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Loosen or rewrite this if the filter excluded too many candidates. Blank = no type filter.
        </p>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium">Additional context</label>
        <textarea
          value={additional}
          onChange={(e) => setAdditional(e.target.value)}
          rows={5}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] leading-[1.6]"
          placeholder="Anything about sector, structure, or specific investor types we should focus on."
        />
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {friendlyAnthropicErrorMessage(error)}
        </div>
      )}

      <div className="flex gap-3">
        <button
          disabled={busy}
          onClick={handleRefine}
          className="rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {busy ? "Refining…" : "Refine and continue"}
        </button>
        <button
          disabled={busy}
          onClick={handleProceedAnyway}
          className="rounded-md border border-input bg-background px-5 py-2.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
        >
          Continue with what we found
        </button>
        <button
          disabled={busy}
          onClick={handleCancel}
          className="rounded-md border border-input bg-background px-5 py-2.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
